import React from "react";

import {
  Editor,
  EditorState,
  Modifier,
  RichUtils,
  getDefaultKeyBinding,
  SelectionState,
  CharacterMetadata,
  EditorBlock,
  ContentState,
  ContentBlock,
  genKey,
  convertToRaw,
  convertFromRaw,
} from "draft-js";
import isSoftNewlineEvent from "draft-js/lib/isSoftNewlineEvent";
import {
  getSelectedBlocksList,
  changeDepth,
  getSelectedBlock,
  getAllBlocks,
  getSelectedBlocksMetadata,
  setBlockData,
  removeAllInlineStyles,
} from "draftjs-utils";

import {
  getSelectionRange,
  getSelectionCoords,
  blockMapBuid,
  blockTableTools,
  tableHandler,
} from "./utils";
import { decorator } from "./decorator";
import { stylemap, FONT_SIZES, FONT_COLORS, PRESETS, BLOCKS } from "./stylemap";

import "draft-js/dist/Draft.css";
import "../../../sass/editor.scss";
import "../../../sass/resizable.scss";
import EntityDialog from "./EntityDialog";
import EditorTableDialog from "./EditorTableDialog";
import PageDialog from "./PageDialog";
import EditorGridCell from "./EditorGridCell";
import { Map, OrderedMap, List } from "immutable";
import Context from "../../Context";

import compressor from "./compressor";
import toHTML from "./toHTML";
import fromHTML from "./fromHTML";
import { sanitizePastedHTML } from "./wordPaste";
import CrumbsProvider from "../Misc/CrumbsProvider";
import ToolbarA from "./ToolbarA";
import ToolbarB from "./ToolbarB";
import TableBlock from "./TableBlock";
import TableCell from "./TableCell";

import tableStructure from "./tableStructure";
import ParagraphDialog from "./ParagraphDialog";
import BsModal from "../Misc/BsModal";

const PORTRAIT_EDITOR_WIDTH = 931;
const LANDSCAPE_EDITOR_WIDTH = 1139;
const GRID_COLUMNS_COUNT = 12;

export default class TemplateEditor extends React.Component {
  static contextType = Context;
  constructor(props) {
    super(props);
    this.state = {
      editorState: this.initContent(),
      editorStateChanged: false,
      pending: false,
      autoSaveStatus: props.autoSaveRestored ? "restored" : "idle",
      formatPainter: null,
      findReplace: {
        open: false,
        query: "",
        replacement: "",
        caseSensitive: false,
        activeIndex: 0,
      },

      selectedCells: [],
      selectedBlocks: [],
      selectedBlocksTypes: [],
      currentBlock: {},
      currentTable: false,
      tableContextMenu: null,
      tableResizePreview: null,

      highlightedEntityKey: "",
      selectedEntity: null,

      showEntityDialog: false,
      showTableDialog: false,
      showPageDialog: false,
      showParagraphDialog: false,

      editorFocused: false,

      pageSettings: { orientation: "portrait" },
      currentEditorHeight: null,
      editorWidth: PORTRAIT_EDITOR_WIDTH,
      columnWidth: Math.ceil(PORTRAIT_EDITOR_WIDTH / GRID_COLUMNS_COUNT) - 1,
      currentScale: "100",

      preview: false,

      blockTables: {},
      inlineToolbar: {
        show: false,
        position: {
          top: 0,
          left: 0,
        },
      },
    };
    this.editorComponentRef = React.createRef();
    this.editorRef = React.createRef();
    this.horizontalRulerRef = React.createRef();
    this.autoSaveTimer = null;
    this.autoSaveRevision = 0;
    this.tableDragAnchorKey = null;
    this.tableDragInitialSelection = [];
    this.tableDragAdditive = false;
    this.tableDragWasMoved = false;
    this.tableDragAxis = null;
    this.tableDragAnchorRect = null;
    this.tableDragTableElement = null;
    this.tableDragStartPoint = null;
    this.suppressNextTableClick = false;
    this.tableResizeSession = null;
    this.formatPainterMouseUpTimer = null;
    this.rulerDragValues = null;
  }

  componentDidMount() {
    window.addEventListener("paste", this.handleWindowPaste);
    window.addEventListener("beforeunload", this.flushAutoSave);
    window.addEventListener("mousedown", this.onTableContextMenuOutside);
    window.addEventListener("keydown", this.onTableContextMenuKeyDown);
    window.addEventListener("mouseup", this.onTableCellMouseUp);
    window.addEventListener("mousemove", this.onTableResizeMove);
    window.addEventListener("mousemove", this.onRulerDragMove);
    window.addEventListener("mouseup", this.onRulerDragEnd);
    window.addEventListener("scroll", this.syncRulerStickyOffset, true);
    window.addEventListener("resize", this.syncRulerStickyOffset);
    this.syncRulerStickyOffset();
  }
  componentDidUpdate(prevProps, prevState) {
    if (
      prevState.editorState.getCurrentContent() !==
      this.state.editorState.getCurrentContent()
    ) {
      this.queueAutoSave();
    }
    this.syncRulerStickyOffset();
  }
  componentWillUnmount() {
    window.removeEventListener("paste", this.handleWindowPaste);
    window.removeEventListener("beforeunload", this.flushAutoSave);
    window.removeEventListener("mousedown", this.onTableContextMenuOutside);
    window.removeEventListener("keydown", this.onTableContextMenuKeyDown);
    window.removeEventListener("mouseup", this.onTableCellMouseUp);
    window.removeEventListener("mousemove", this.onTableResizeMove);
    window.removeEventListener("mousemove", this.onRulerDragMove);
    window.removeEventListener("mouseup", this.onRulerDragEnd);
    window.removeEventListener("scroll", this.syncRulerStickyOffset, true);
    window.removeEventListener("resize", this.syncRulerStickyOffset);
    if (this.formatPainterMouseUpTimer) {
      window.clearTimeout(this.formatPainterMouseUpTimer);
    }
    this.flushAutoSave();
  }

  initContent = () => {
    if (this.props.content) {
      return EditorState.createWithContent(
        convertFromRaw(JSON.parse(this.props.content)),
        decorator
      );
    }
    if (this.props.html) {
      return EditorState.createWithContent(
        fromHTML(this.props.html || ""),
        decorator
      );
    }

    return EditorState.createEmpty(decorator);
  };

  onChange = (editorState, callbackFn = false) => {
    editorState = this.limitUndoHistory(editorState);
    const contentChanged =
      editorState.getCurrentContent() !==
      this.state.editorState.getCurrentContent();
    this.setState(
      {
        editorState,
        editorStateChanged: this.state.editorStateChanged || contentChanged,
      },
      () => {
        this.highlightBlocks();
        let block = getSelectedBlock(editorState);
        let currentBlock = block
          ? {
              key: block.getKey(),
              type: block.getType(),
              data: block.getData().toJS(),
              length: block.getLength(),
            }
          : null;
        this.setState({ currentBlock });
        this.adjustEditorHeight();
        if (callbackFn) callbackFn();
      }
    );
  };

  limitUndoHistory = (editorState) => {
    const undoStack = editorState.getUndoStack();
    if (undoStack.size <= 10) return editorState;

    return EditorState.set(editorState, { undoStack: undoStack.take(10) });
  };

  onUndo = () => {
    const { editorState } = this.state;
    if (editorState.getUndoStack().size === 0) return;
    this.onChange(EditorState.undo(editorState), () => {
      if (this.editorComponentRef.current) this.editorComponentRef.current.focus();
    });
  };

  onRedo = () => {
    const { editorState } = this.state;
    if (editorState.getRedoStack().size === 0) return;
    this.onChange(EditorState.redo(editorState), () => {
      if (this.editorComponentRef.current) this.editorComponentRef.current.focus();
    });
  };

  handleKeyCommand = (command) => {
    if (command === "undo") {
      this.onUndo();
      return "handled";
    }
    if (command === "redo") {
      this.onRedo();
      return "handled";
    }
    if (command === "format-painter-copy") {
      this.captureFormatPainter();
      return "handled";
    }
    if (command === "format-painter-apply") {
      this.applyFormatPainter();
      return "handled";
    }
    if (command === "format-painter-cancel") {
      this.cancelFormatPainter();
      return "handled";
    }
    if (command === "find-replace") {
      this.openFindReplace();
      return "handled";
    }
    return "not-handled";
  };

  queueAutoSave = () => {
    if (!this.props.onAutoSave || this.props.readOnly) return;

    if (this.autoSaveTimer) window.clearTimeout(this.autoSaveTimer);
    this.autoSaveRevision += 1;
    const revision = this.autoSaveRevision;
    this.setState({ autoSaveStatus: "waiting" });
    this.autoSaveTimer = window.setTimeout(() => {
      this.autoSaveTimer = null;
      this.performAutoSave(revision);
    }, 1200);
  };

  performAutoSave = async (revision, silent = false) => {
    if (!this.props.onAutoSave) return false;

    const contentState = this.state.editorState.getCurrentContent();
    const content = convertToRaw(contentState);
    const html = await this.htmlSave(contentState);

    if (!silent) this.setState({ autoSaveStatus: "saving" });
    const saved = await this.props.onAutoSave(content, html);

    if (!silent && revision === this.autoSaveRevision) {
      this.setState({ autoSaveStatus: saved === false ? "error" : "saved" });
    }
    return saved;
  };

  flushAutoSave = () => {
    if (!this.autoSaveTimer) return;
    window.clearTimeout(this.autoSaveTimer);
    this.autoSaveTimer = null;
    this.performAutoSave(this.autoSaveRevision, true);
  };

  getAutoSaveText = () => {
    const labels = {
      waiting: "Временный черновик: ожидает сохранения",
      saving: "Сохранение временного черновика…",
      saved: "Временный черновик сохранён",
      restored: "Восстановлен временный черновик",
      discarded: "Временный черновик удалён",
      published: "Документ сохранён",
      error: "Не удалось сохранить временный черновик",
    };
    return labels[this.state.autoSaveStatus] || "";
  };

  onDiscardAutoSave = () => {
    const discarded = this.props.onDiscardAutoSave();
    if (discarded !== false) this.setState({ autoSaveStatus: "discarded" });
  };

  /* ------ Focus, Blur & Highlights ------ */

  //Editor gets focus
  onFocus = () => {
    //Update state and highlight blocks
    this.setState({ editorFocused: true });
    this.highlightBlocks();
  };

  //Editor loses focus
  onBlur = () => {
    //Update state if no dialogs arw shown
    if (
      this.state.showEntityDialog ||
      this.state.showTableDialog ||
      this.state.showParagraphDialog ||
      this.showPageDialog
    )
      return;
    this.setState({ editorFocused: false });
  };

  //Remove highlight from all blocks (DOM)
  highlightBlocksBlur = () => {
    document
      .querySelectorAll(`.editor-block`)
      .forEach((elem) => elem.classList.remove("focus"));
  };

  //Hightlight currently selected blocks (DOM) (compare with state)
  highlightBlocks = () => {
    let blockKeys = [];
    let blockTypes = [];
    let blocksMap = getSelectedBlocksList(this.state.editorState);
    if (blocksMap) {
      for (let block of blocksMap.values()) {
        if (block) {
          blockKeys.push(block.getKey());
          blockTypes.push(block.getType());
        }
      }
      if (
        JSON.stringify(this.state.selectedBlocks) === JSON.stringify(blockKeys)
      )
        return;
      this.setState({
        selectedBlocks: blockKeys,
        selectedBlocksTypes: blockTypes,
      });
      this.highlightBlocksBlur();
      for (let key of blockKeys) {
        var container = document.querySelector(
          `.editor-block[data-offset-key="${key}-0-0"]`
        );
        if (container) container.classList.add("focus");
      }
    }
  };

  highlightCells = () => {
    document
      .querySelectorAll(`td.editor-table-cell`)
      .forEach((elem) => elem.classList.remove("selected"));
    this.state.selectedCells.forEach((key) => {
      var container = document.querySelector(`#editor-table-cell-${key}`);
      if (container) container.classList.add("selected");
    });
  };

  applyPageSettings = (settings) => {
    let pageSettings = settings || this.state.pageSettings;
    let editorWidth =
      pageSettings.orientation === "landscape"
        ? LANDSCAPE_EDITOR_WIDTH
        : PORTRAIT_EDITOR_WIDTH;
    let result = {
      pageSettings,

      editorWidth: editorWidth,
      columnWidth: Math.ceil(editorWidth / GRID_COLUMNS_COUNT) - 1,
      currentScale: "75",
    };

    this.setState(result, () => this.adjustEditorHeight());
  };

  //Highight Entity block by key (DOM only) (compare with state)
  highlightEntity = (entityKey) => {
    if (this.state.highlightedEntityKey !== entityKey) {
      for (let elem of document.querySelectorAll(".editor-entity")) {
        elem.classList.remove("dom-selected");
        if (elem.dataset.entity === entityKey)
          elem.classList.add("dom-selected");
      }
      this.setState({ highlightedEntityKey: entityKey });
    }
  };

  /*  ------ Catch changes by toolbars ------ */

  //Toggle inline styles and solve conflicts
  toggleInlineStyle = (setStyles, callbackFn = false, selection = false) => {
    let editorState = this.state.editorState;
    const currentStyle = editorState.getCurrentInlineStyle();
    if (!Array.isArray(setStyles)) setStyles = [setStyles];

    if (selection) {
      editorState = EditorState.forceSelection(editorState, selection);
    }

    const conflictStyles = [
      ["LH15", "LH2", "LH25"],
      ["RIGHT", "LEFT", "CENTER"],
      ["fontTimes", "fontArial", "fontCourier", "fontComic"],
    ];

    let fontSizesConflict = [];
    FONT_SIZES.forEach((size) => {
      fontSizesConflict.push(`fontSize${size}`);
    });
    conflictStyles.push(fontSizesConflict);

    let fontColorsConflict = [];
    FONT_COLORS.forEach((color) => {
      fontColorsConflict.push(`fontColor${color}`);
    });
    conflictStyles.push(fontColorsConflict);

    conflictStyles.forEach((conflict) => {
      let crossedValues = [...new Set(setStyles)].filter((item) =>
        conflict.includes(item)
      );
      if (crossedValues.length) {
        conflict.forEach((conflictStyle) => {
          if (currentStyle.has(conflictStyle)) {
            editorState = RichUtils.toggleInlineStyle(
              editorState,
              conflictStyle
            );
          }
        });
      }
    });
    setStyles.forEach((inlineStyle) => {
      editorState = RichUtils.toggleInlineStyle(editorState, inlineStyle);
    });

    if (editorState !== this.state.editorState)
      this.onChange(editorState, callbackFn);
  };

  //Togggle block types with some indent/outdent hacks
  toggleBlockType = (blockType) => {
    let callbackFn = false;
    const type = this.state.currentBlock.type;
    const isList =
      type === "unordered-list-item" || type === "ordered-list-item";

    if (blockType === "indent") {
      if (isList) {
        this.onChange(changeDepth(this.state.editorState, 1, 4));
      } else {
        this.blockIndent();
      }
      return;
    }
    if (blockType === "outdent") {
      if (isList) {
        this.onChange(changeDepth(this.state.editorState, -1, 4));
      } else {
        this.blockOutdent();
      }
      return;
    }

    if (blockType === "table") {
      this.createTable();
      return;
    }

    if (blockType === "paragraph") {
      this.setState({ showParagraphDialog: true });
      return;
    }

    this.onChange(
      RichUtils.toggleBlockType(this.state.editorState, blockType),
      callbackFn
    );
  };

  createTable = (rows = 2, columns = 2) => {
    if (this.state.selectedCells.length) {
      this.onTableDialogShow();
      return;
    }
    this.tableBuild(tableStructure.create(rows, columns));
  };

  togglePreset = (preset) => {
    let editorState = this.state.editorState;
    let selection = editorState.getSelection();
    let contentState = editorState.getCurrentContent();
    const wholeBlock = selection.isCollapsed();
    if (wholeBlock) {
      selection = SelectionState.createEmpty(this.state.currentBlock.key);
      selection = selection.merge({
        focusOffset: this.state.currentBlock.length,
      });
    }

    Object.keys(stylemap).forEach((style) => {
      contentState = Modifier.removeInlineStyle(contentState, selection, style);
    });

    preset.styles.forEach((style) => {
      contentState = Modifier.applyInlineStyle(contentState, selection, style);
    });

    contentState = Modifier.mergeBlockData(
      contentState,
      selection,
      preset.blockData
    );

    editorState = EditorState.push(
      editorState,
      contentState,
      "block-clear-preset"
    );

    this.onChange(editorState);
  };

  formatPainterBlockDataKeys = [
    "align",
    "paddingLeft",
    "paddingRight",
    "paddingTop",
    "paddingBottom",
    "firstLineIndent",
    "lineHeight",
  ];

  isTableBlock = (block) =>
    block && (block.getType() === "table" || block.getType() === "cell");

  getFormatPainterSelection = (editorState) => {
    const selection = editorState.getSelection();
    if (!selection.isCollapsed()) return selection;

    const block = editorState
      .getCurrentContent()
      .getBlockForKey(selection.getStartKey());
    if (!block) return selection;

    return SelectionState.createEmpty(block.getKey()).merge({
      anchorOffset: 0,
      focusOffset: block.getLength(),
    });
  };

  getFormatPainterBlockKeys = (contentState, selection) => {
    const keys = [];
    const endKey = selection.getEndKey();
    let key = selection.getStartKey();
    while (key) {
      keys.push(key);
      if (key === endKey) break;
      key = contentState.getKeyAfter(key);
    }
    return keys;
  };

  captureFormatPainter = (persistent = false) => {
    const { editorState } = this.state;
    const selection = editorState.getSelection();
    const block = editorState
      .getCurrentContent()
      .getBlockForKey(selection.getStartKey());
    if (!block) return;

    const characterOffset = Math.max(
      0,
      Math.min(selection.getStartOffset(), Math.max(0, block.getLength() - 1))
    );
    const character = block.getCharacterList().get(characterOffset);
    const inlineStyles = character
      ? character.getStyle().toArray()
      : editorState.getCurrentInlineStyle().toArray();
    const blockData = block.getData().toJS();

    this.setState({
      formatPainter: {
        inlineStyles: inlineStyles.filter((style) => stylemap[style]),
        blockType: this.isTableBlock(block) ? null : block.getType(),
        blockDepth: this.isTableBlock(block) ? null : block.getDepth(),
        blockData: this.formatPainterBlockDataKeys.reduce((result, key) => {
          if (blockData[key] !== undefined) result[key] = blockData[key];
          return result;
        }, {}),
        persistent,
      },
    });
  };

  onFormatPainterButton = (persistent = false) => {
    if (!persistent && this.state.formatPainter) {
      this.setState({ formatPainter: null });
      return;
    }
    this.captureFormatPainter(persistent);
  };

  cancelFormatPainter = () => {
    if (this.state.formatPainter) this.setState({ formatPainter: null });
  };

  getFindResults = () => {
    const { query, caseSensitive } = this.state.findReplace;
    if (!query) return [];

    const needle = caseSensitive ? query : query.toLocaleLowerCase();
    const results = [];
    this.state.editorState.getCurrentContent().getBlockMap().forEach((block) => {
      const text = block.getText();
      const searchable = caseSensitive ? text : text.toLocaleLowerCase();
      let start = 0;
      while (start <= searchable.length) {
        const index = searchable.indexOf(needle, start);
        if (index === -1) break;
        results.push({
          blockKey: block.getKey(),
          start: index,
          end: index + query.length,
          preview: text,
        });
        start = index + Math.max(1, query.length);
      }
    });
    return results;
  };

  openFindReplace = () => {
    this.setState((state) => ({
      findReplace: { ...state.findReplace, open: true },
    }));
  };

  closeFindReplace = () => {
    this.setState((state) => ({
      findReplace: { ...state.findReplace, open: false },
    }));
  };

  updateFindReplace = (key, value) => {
    this.setState((state) => ({
      findReplace: { ...state.findReplace, [key]: value, activeIndex: 0 },
    }));
  };

  selectFindResult = (index) => {
    const result = this.getFindResults()[index];
    if (!result) return;
    const selection = SelectionState.createEmpty(result.blockKey).merge({
      anchorOffset: result.start,
      focusOffset: result.end,
    });
    this.setState((state) => ({
      findReplace: { ...state.findReplace, activeIndex: index },
    }));
    this.onChange(EditorState.forceSelection(this.state.editorState, selection), () => {
      if (this.editorComponentRef.current) this.editorComponentRef.current.focus();
    });
  };

  replaceFindResult = (index = this.state.findReplace.activeIndex) => {
    const result = this.getFindResults()[index];
    if (!result) return;
    const { editorState, findReplace } = this.state;
    const selection = SelectionState.createEmpty(result.blockKey).merge({
      anchorOffset: result.start,
      focusOffset: result.end,
    });
    const block = editorState.getCurrentContent().getBlockForKey(result.blockKey);
    const character = block && block.getCharacterList().get(result.start);
    const content = Modifier.replaceText(
      editorState.getCurrentContent(),
      selection,
      findReplace.replacement,
      character ? character.getStyle() : undefined
    );
    this.onChange(EditorState.push(editorState, content, "insert-characters"), () => {
      this.selectFindResult(Math.min(index, this.getFindResults().length - 1));
    });
  };

  replaceAllFindResults = () => {
    const results = this.getFindResults();
    if (!results.length) return;
    const { editorState, findReplace } = this.state;
    let content = editorState.getCurrentContent();
    [...results].reverse().forEach((result) => {
      const selection = SelectionState.createEmpty(result.blockKey).merge({
        anchorOffset: result.start,
        focusOffset: result.end,
      });
      const block = content.getBlockForKey(result.blockKey);
      const character = block && block.getCharacterList().get(result.start);
      content = Modifier.replaceText(
        content,
        selection,
        findReplace.replacement,
        character ? character.getStyle() : undefined
      );
    });
    this.onChange(EditorState.push(editorState, content, "insert-characters"), () => {
      this.setState((state) => ({
        findReplace: { ...state.findReplace, activeIndex: 0 },
      }));
    });
  };

  renderFindReplace = () => {
    const { findReplace } = this.state;
    if (!findReplace.open) return null;
    const results = this.getFindResults();
    const scale = Number(this.state.currentScale || 100) / 100;
    const panelLeft = (this.state.editorWidth + 60) * scale / 2 + 252;

    return (
      <aside className="editor-find-replace" style={{ left: `calc(50% - ${panelLeft}px)` }}>
        <div className="editor-find-replace__header">
          <strong>Поиск и замена</strong>
          <button type="button" aria-label="Закрыть поиск и замену" onClick={this.closeFindReplace}>×</button>
        </div>
        <label>
          Найти
          <input
            autoFocus
            value={findReplace.query}
            onChange={(event) => this.updateFindReplace("query", event.target.value)}
            placeholder="Введите текст"
          />
        </label>
        <label>
          Заменить на
          <input
            value={findReplace.replacement}
            onChange={(event) => this.updateFindReplace("replacement", event.target.value)}
            placeholder="Введите замену"
          />
        </label>
        <label className="editor-find-replace__case">
          <input
            type="checkbox"
            checked={findReplace.caseSensitive}
            onChange={(event) => this.updateFindReplace("caseSensitive", event.target.checked)}
          />
          Учитывать регистр
        </label>
        <div className="editor-find-replace__actions">
          <button type="button" disabled={!results.length} onClick={() => this.replaceFindResult()}>Заменить</button>
          <button type="button" disabled={!results.length} onClick={this.replaceAllFindResults}>Заменить всё</button>
        </div>
        <div className="editor-find-replace__results" aria-live="polite">
          <span>{findReplace.query ? `Совпадений: ${results.length}` : "Введите текст для поиска"}</span>
          {results.map((result, index) => (
            <button
              type="button"
              key={`${result.blockKey}-${result.start}`}
              className={index === findReplace.activeIndex ? "is-active" : ""}
              onClick={() => this.selectFindResult(index)}
            >
              {result.preview}
            </button>
          ))}
        </div>
      </aside>
    );
  };

  onEditorMouseUp = (event) => {
    if (
      event.button !== 0 ||
      !this.state.formatPainter ||
      this.formatPainterMouseUpTimer
    ) {
      return;
    }

    // Draft.js updates its selection after the native mouseup handler. Wait for
    // that update, then apply only to a real drag selection (not a simple click).
    this.formatPainterMouseUpTimer = window.setTimeout(() => {
      this.formatPainterMouseUpTimer = null;
      const selection = this.state.editorState.getSelection();
      if (this.state.formatPainter && !selection.isCollapsed()) {
        this.applyFormatPainter();
      }
    }, 0);
  };

  applyFormatPainter = () => {
    const format = this.state.formatPainter;
    if (!format) return;

    const { editorState } = this.state;
    const targetSelection = this.getFormatPainterSelection(editorState);
    let contentState = editorState.getCurrentContent();
    const selectedBlockKeys = this.getFormatPainterBlockKeys(
      contentState,
      targetSelection
    );

    Object.keys(stylemap).forEach((style) => {
      contentState = Modifier.removeInlineStyle(contentState, targetSelection, style);
    });
    format.inlineStyles.forEach((style) => {
      contentState = Modifier.applyInlineStyle(contentState, targetSelection, style);
    });

    const blockMap = contentState.getBlockMap().withMutations((map) => {
      selectedBlockKeys.forEach((key) => {
        const block = map.get(key);
        if (!block || this.isTableBlock(block)) return;

        let blockData = block.getData();
        this.formatPainterBlockDataKeys.forEach((dataKey) => {
          blockData = blockData.remove(dataKey);
        });
        Object.entries(format.blockData).forEach(([dataKey, value]) => {
          blockData = blockData.set(dataKey, value);
        });

        let nextBlock = block.set("data", blockData);
        if (format.blockType) nextBlock = nextBlock.set("type", format.blockType);
        if (format.blockDepth !== null) {
          nextBlock = nextBlock.set("depth", format.blockDepth);
        }
        map.set(key, nextBlock);
      });
    });
    contentState = contentState.merge({ blockMap });

    let nextEditorState = EditorState.push(
      editorState,
      contentState,
      "change-inline-style"
    );
    nextEditorState = EditorState.forceSelection(nextEditorState, targetSelection);
    this.onChange(nextEditorState, () => {
      if (!format.persistent) this.setState({ formatPainter: null });
      if (this.editorComponentRef.current) this.editorComponentRef.current.focus();
    });
  };

  //Toggles & merges block data by toolbar
  onToggleBlockData = (dataStyle) => {
    let editorState = this.state.editorState;
    let blockData = Object.assign(dataStyle, {});
    if (this.state.selectedCells.length) {
      this.blocksFewMergeData(
        [...new Set(this.state.selectedCells)],
        blockData,
        editorState.getSelection()
      );
      return;
    }
    const newContentState = Modifier.mergeBlockData(
      editorState.getCurrentContent(),
      editorState.getSelection(),
      blockData
    );
    editorState = EditorState.push(
      editorState,
      newContentState,
      "change-block-data"
    );
    if (editorState !== this.state.editorState) this.onChange(editorState);
  };

  //Calls custom function by toolbar call
  onCallFunction = (id) => {
    switch (id) {
      case "table-select-row":
        this.tableSelectRow();
        break;
      case "table-select-col":
        this.tableSelectCol();
        break;
      case "table-autofit-col":
        this.tableAutoFitCurrent("column");
        break;
      case "table-autofit-row":
        this.tableAutoFitCurrent("row");
        break;
      case "table-border-settings":
        this.onTableDialogShow();
        break;
      case "table-border-all":
        this.tableApplyBorderPreset("all");
        break;
      case "table-border-none":
        this.tableApplyBorderPreset("none");
        break;
      case "table-border-outer":
        this.tableApplyBorderPreset("outer");
        break;
      case "table-border-inner":
        this.tableApplyBorderPreset("inner");
        break;
      case "table-border-inner-horizontal":
        this.tableApplyBorderPreset("inner-horizontal");
        break;
      case "table-border-inner-vertical":
        this.tableApplyBorderPreset("inner-vertical");
        break;
      case "table-remove-row":
        this.tableRowRemove();
        break;
      case "table-remove-col":
        this.tableColRemove();
        break;
      case "table-remove":
        this.tableRemove();
        break;
      case "table-merge-cells":
        this.tableMergeCells();
        break;
      case "table-split-cell":
        this.tableSplitCell();
        break;
      case "table-add-down":
        this.tableRowColAdd("down");
        break;
      case "table-add-up":
        this.tableRowColAdd("up");
        break;
      case "table-add-left":
        this.tableRowColAdd("left");
        break;
      case "table-add-right":
        this.tableRowColAdd("right");
        break;
      default:
        break;
    }
  };

  onTableContextMenu = (event, cellKey) => {
    event.preventDefault();
    event.stopPropagation();
    const { editorState } = this.state;
    const block = editorState.getCurrentContent().getBlockForKey(cellKey);
    if (!block || (block.getType() !== "table" && block.getType() !== "cell")) {
      return;
    }

    const menuWidth = 236;
    const collapsedMenuHeight = 260;
    const left = Math.max(8, Math.min(event.clientX, window.innerWidth - menuWidth));
    const top = Math.max(
      8,
      Math.min(event.clientY, window.innerHeight - collapsedMenuHeight)
    );
    const maxHeight = Math.max(160, window.innerHeight - top - 8);
    const currentBlock = {
      key: block.getKey(),
      type: block.getType(),
      data: block.getData().toJS(),
      length: block.getLength(),
    };

    this.setState(
      {
        editorState: EditorState.forceSelection(
          editorState,
          SelectionState.createEmpty(cellKey)
        ),
        currentBlock,
        selectedCells:
          this.state.selectedCells.length > 1 &&
          this.state.selectedCells.includes(cellKey)
            ? this.state.selectedCells
            : [cellKey],
        tableContextMenu: { left, top, maxHeight },
      },
      () => this.highlightCells()
    );
  };

  onTableContextMenuOutside = (event) => {
    if (
      this.state.tableContextMenu &&
      (!event.target.closest || !event.target.closest(".table-context-menu"))
    ) {
      this.setState({ tableContextMenu: null });
    }
  };

  onTableContextMenuKeyDown = (event) => {
    if (event.key === "Escape" && this.state.tableContextMenu) {
      this.setState({ tableContextMenu: null });
    }
  };

  onTableContextMenuAction = (id) => {
    this.setState({ tableContextMenu: null }, () => this.onCallFunction(id));
  };

  tableGetInfoForCell = (cellKey) => {
    const contentState = this.state.editorState.getCurrentContent();
    const block = contentState.getBlockForKey(cellKey);
    if (!block) return false;
    const currentBlock = {
      key: block.getKey(),
      type: block.getType(),
      data: block.getData().toJS(),
      length: block.getLength(),
    };
    const table = this.tableGetForBlock(currentBlock);
    if (!Array.isArray(table)) return false;
    const cellLocation = tableStructure.cellLocate(table, cellKey);
    if (!Number.isInteger(cellLocation.row) || !Number.isInteger(cellLocation.col)) {
      return false;
    }
    return { table, currentBlock, cellLocation };
  };

  onTableCellMouseDown = (event, cellKey) => {
    if (this.tableResizeSession) return;
    if (event.button !== 0) return;
    const info = this.tableGetInfoForCell(cellKey);
    if (!info) return;
    this.tableDragAnchorKey = cellKey;
    this.tableDragAdditive = Boolean(event.ctrlKey || event.metaKey);
    this.tableDragWasMoved = false;
    this.tableDragAxis = null;
    this.tableDragStartPoint =
      Number.isFinite(event.clientX) && Number.isFinite(event.clientY)
        ? { x: event.clientX, y: event.clientY }
        : null;
    const anchorElement = document.getElementById(`editor-table-cell-${cellKey}`);
    this.tableDragAnchorRect = anchorElement
      ? anchorElement.getBoundingClientRect()
      : null;
    this.tableDragTableElement = anchorElement
      ? anchorElement.closest("table")
      : null;
    this.tableDragInitialSelection = this.tableDragAdditive
      ? [...this.state.selectedCells]
      : [];
    const selectedCells = this.tableDragAdditive
      ? [...new Set([...this.tableDragInitialSelection, cellKey])]
      : [cellKey];
    this.setState(
      { currentBlock: info.currentBlock, selectedCells },
      () => this.highlightCells()
    );
  };

  onTableCellMouseEnter = (event, cellKey) => {
    if (!this.tableDragAnchorKey || event.buttons !== 1) return;
    const anchorInfo = this.tableGetInfoForCell(this.tableDragAnchorKey);
    const targetInfo = this.tableGetInfoForCell(cellKey);
    if (
      !anchorInfo ||
      !targetInfo ||
      anchorInfo.table[0][0].key !== targetInfo.table[0][0].key
    )
      return;
    if (this.tableDragAnchorKey === cellKey) return;

    const anchorRow = anchorInfo.cellLocation.row;
    const anchorCol = anchorInfo.cellLocation.col;
    const targetRow = targetInfo.cellLocation.row;
    const targetCol = targetInfo.cellLocation.col;
    const targetElement = document.getElementById(`editor-table-cell-${cellKey}`);
    const targetRect = targetElement && targetElement.getBoundingClientRect();
    if (!this.tableDragAxis) {
      if (
        this.tableDragStartPoint &&
        Number.isFinite(event.clientX) &&
        Number.isFinite(event.clientY)
      ) {
        const horizontalDistance = Math.abs(event.clientX - this.tableDragStartPoint.x);
        const verticalDistance = Math.abs(event.clientY - this.tableDragStartPoint.y);
        this.tableDragAxis =
          horizontalDistance > verticalDistance
            ? "row"
            : verticalDistance > horizontalDistance
            ? "column"
            : "rectangle";
      } else if (this.tableDragAnchorRect && targetRect) {
        const horizontalDistance = Math.abs(
          targetRect.left + targetRect.width / 2 -
            (this.tableDragAnchorRect.left + this.tableDragAnchorRect.width / 2)
        );
        const verticalDistance = Math.abs(
          targetRect.top + targetRect.height / 2 -
            (this.tableDragAnchorRect.top + this.tableDragAnchorRect.height / 2)
        );
        this.tableDragAxis =
          horizontalDistance > verticalDistance
            ? "row"
            : verticalDistance > horizontalDistance
            ? "column"
            : "rectangle";
      } else if (anchorRow === targetRow && anchorCol !== targetCol) {
        this.tableDragAxis = "row";
      } else if (anchorCol === targetCol && anchorRow !== targetRow) {
        this.tableDragAxis = "column";
      } else {
        this.tableDragAxis = "rectangle";
      }
    }
    const minRow =
      this.tableDragAxis === "row"
        ? anchorRow
        : Math.min(anchorRow, targetRow);
    const maxRow =
      this.tableDragAxis === "row"
        ? anchorRow
        : Math.max(anchorRow, targetRow);
    const minCol =
      this.tableDragAxis === "column"
        ? anchorCol
        : Math.min(anchorCol, targetCol);
    const maxCol =
      this.tableDragAxis === "column"
        ? anchorCol
        : Math.max(anchorCol, targetCol);
    const hasVisualSelection = Boolean(
      this.tableDragAnchorRect && targetRect && this.tableDragTableElement
    );
    let selectedCells = this.tableGetVisibleDragSelection(targetRect);
    if (!selectedCells.length && !hasVisualSelection) {
      selectedCells = [];
      for (let row = minRow; row <= maxRow; row++) {
        for (let col = minCol; col <= maxCol; col++) {
          selectedCells.push(targetInfo.table[row][col].key);
        }
      }
    }
    this.tableDragWasMoved = true;
    const nextSelectedCells = this.tableDragAdditive
      ? [...new Set([...this.tableDragInitialSelection, ...selectedCells])]
      : selectedCells;
    this.setState(
      { currentBlock: targetInfo.currentBlock, selectedCells: nextSelectedCells },
      () => this.highlightCells()
    );
  };

  onTableCellMouseUp = () => {
    if (this.tableDragWasMoved) {
      this.suppressNextTableClick = true;
      window.setTimeout(() => {
        this.suppressNextTableClick = false;
      }, 0);
    }
    this.tableDragAnchorKey = null;
    this.tableDragInitialSelection = [];
    this.tableDragAdditive = false;
    this.tableDragWasMoved = false;
    this.tableDragAxis = null;
    this.tableDragAnchorRect = null;
    this.tableDragTableElement = null;
    this.tableDragStartPoint = null;
    this.onTableResizeEnd();
  };

  tableGetVisibleDragSelection = (targetRect) => {
    if (!this.tableDragAnchorRect || !targetRect || !this.tableDragTableElement) {
      return [];
    }
    const anchorCenterX =
      this.tableDragAnchorRect.left + this.tableDragAnchorRect.width / 2;
    const anchorCenterY =
      this.tableDragAnchorRect.top + this.tableDragAnchorRect.height / 2;
    const targetCenterX = targetRect.left + targetRect.width / 2;
    const targetCenterY = targetRect.top + targetRect.height / 2;
    const minX = Math.min(anchorCenterX, targetCenterX);
    const maxX = Math.max(anchorCenterX, targetCenterX);
    const minY = Math.min(anchorCenterY, targetCenterY);
    const maxY = Math.max(anchorCenterY, targetCenterY);

    return Array.from(
      this.tableDragTableElement.querySelectorAll("td.editor-table-cell[id]")
    )
      .filter((cell) => {
        const rect = cell.getBoundingClientRect();
        const centerX = rect.left + rect.width / 2;
        const centerY = rect.top + rect.height / 2;
        if (this.tableDragAxis === "column") {
          return (
            rect.left <= anchorCenterX &&
            rect.right >= anchorCenterX &&
            centerY >= minY &&
            centerY <= maxY
          );
        }
        if (this.tableDragAxis === "row") {
          return (
            rect.top <= anchorCenterY &&
            rect.bottom >= anchorCenterY &&
            centerX >= minX &&
            centerX <= maxX
          );
        }
        return (
          centerX >= minX &&
          centerX <= maxX &&
          centerY >= minY &&
          centerY <= maxY
        );
      })
      .map((cell) => cell.id.replace("editor-table-cell-", ""));
  };

  onTableResizeStart = (event, axis, cellKey) => {
    event.preventDefault();
    event.stopPropagation();
    const info = this.tableGetInfoForCell(cellKey);
    const cellElement = document.getElementById(`editor-table-cell-${cellKey}`);
    if (!info || !cellElement) return;

    const cell = info.table[info.cellLocation.row][info.cellLocation.col];
    const tableElement = cellElement.closest("table");
    if (!tableElement) return;
    const tableRect = tableElement.getBoundingClientRect();
    const cellRect = cellElement.getBoundingClientRect();
    if (!tableRect.width) return;

    const widths = tableStructure.getColumnWidths(info.table);
    const rowHeights = tableStructure.getRowHeights(info.table);
    const index =
      axis === "column"
        ? info.cellLocation.col + (cell.colSpan || 1) - 1
        : info.cellLocation.row + (cell.rowSpan || 1) - 1;
    if (axis === "column" && widths.length < 2) return;

    this.tableDragAnchorKey = null;
    this.tableResizeSession = {
      axis,
      cellKey,
      currentBlock: info.currentBlock,
      table: this.tableSyncContent(info.table),
      rootKey: info.table[0][0].key,
      index,
      startX: event.clientX,
      startY: event.clientY,
      tableWidth: tableRect.width,
      tableRect,
      guideStart: axis === "column" ? cellRect.right : cellRect.bottom,
      widths,
      rowHeights,
      startRowHeight: rowHeights[index] || cellElement.getBoundingClientRect().height,
      preview: null,
    };
  };

  onTableResizeMove = (event) => {
    const session = this.tableResizeSession;
    if (!session) return;
    let preview;
    if (session.axis === "column") {
      const widths = [...session.widths];
      const neighbor = session.index < widths.length - 1 ? session.index + 1 : session.index - 1;
      const requestedDelta = ((event.clientX - session.startX) / session.tableWidth) * 100;
      const delta = Math.max(
        5 - widths[session.index],
        Math.min(requestedDelta, widths[neighbor] - 5)
      );
      widths[session.index] += delta;
      widths[neighbor] -= delta;
      preview = {
        rootKey: session.rootKey,
        widths,
        rowHeights: session.rowHeights,
        guideAxis: "column",
        guideIndex: session.index,
        guide: {
          axis: "column",
          left: session.guideStart + event.clientX - session.startX,
          top: session.tableRect.top,
          length: session.tableRect.height,
        },
      };
    } else {
      const rowHeights = [...session.rowHeights];
      rowHeights[session.index] = Math.max(
        24,
        Math.round(session.startRowHeight + event.clientY - session.startY)
      );
      preview = {
        rootKey: session.rootKey,
        widths: session.widths,
        rowHeights,
        guideAxis: "row",
        guideIndex: session.index,
        guide: {
          axis: "row",
          left: session.tableRect.left,
          top: session.guideStart + event.clientY - session.startY,
          length: session.tableRect.width,
        },
      };
    }
    session.preview = preview;
    this.setState({ tableResizePreview: preview });
  };

  onTableResizeEnd = () => {
    const session = this.tableResizeSession;
    if (!session) return;
    this.tableResizeSession = null;
    const preview = session.preview;
    this.setState(
      { tableResizePreview: null, currentBlock: session.currentBlock },
      () => {
        if (!preview) return;
        const table =
          session.axis === "column"
            ? tableStructure.applyColumnWidths(session.table, preview.widths)
            : tableStructure.applyRowHeights(session.table, preview.rowHeights);
        this.tableSave(table, { preserveExistingBlockFormatting: true });
      }
    );
  };

  tableMeasureNaturalCellWidth = (cellElement) => {
    const text = (cellElement.textContent || "").trim();
    if (!text) return 32;

    const documentRef = cellElement.ownerDocument;
    const style = window.getComputedStyle(cellElement);
    const probe = documentRef.createElement("span");
    probe.textContent = text;
    probe.style.cssText = [
      "position: fixed",
      "visibility: hidden",
      "white-space: pre",
      "pointer-events: none",
      `font-family: ${style.fontFamily}`,
      `font-size: ${style.fontSize}`,
      `font-weight: ${style.fontWeight}`,
      `font-style: ${style.fontStyle}`,
      `letter-spacing: ${style.letterSpacing}`,
    ].join(";");
    documentRef.body.appendChild(probe);
    const measuredWidth = probe.getBoundingClientRect().width;
    documentRef.body.removeChild(probe);

    const fallbackWidth = Math.max(...text.split(/\r?\n/).map((line) => line.length)) * 8;
    const padding =
      (parseFloat(style.paddingLeft) || 0) +
      (parseFloat(style.paddingRight) || 0);
    return Math.ceil(Math.max(measuredWidth, fallbackWidth) + padding + 12);
  };

  onTableAutoFit = (axis, cellKey) => {
    const info = this.tableGetInfoForCell(cellKey);
    if (!info) return;

    // A double-click follows a mouse-down used for resizing. Discard that
    // empty resize session so that it cannot overwrite the auto-fit result.
    this.tableResizeSession = null;
    const cell = info.table[info.cellLocation.row][info.cellLocation.col];
    const index =
      axis === "column"
        ? info.cellLocation.col + (cell.colSpan || 1) - 1
        : info.cellLocation.row + (cell.rowSpan || 1) - 1;
    const syncedTable = this.tableSyncContent(info.table);

    if (axis === "row") {
      const rowHeights = tableStructure.getRowHeights(syncedTable);
      rowHeights[index] = null;
      this.setState(
        { currentBlock: info.currentBlock, tableResizePreview: null },
        () =>
          this.tableSave(tableStructure.applyRowHeights(syncedTable, rowHeights), {
            preserveExistingBlockFormatting: true,
          })
      );
      return;
    }

    const cellElement = document.getElementById(`editor-table-cell-${cellKey}`);
    const tableElement = cellElement && cellElement.closest("table");
    const tableWidth = tableElement && tableElement.getBoundingClientRect().width;
    const widths = tableStructure.getColumnWidths(syncedTable);
    if (!tableElement || !tableWidth || widths.length < 2) return;

    const matchingCells = Array.from(
      tableElement.querySelectorAll("td.editor-table-cell[data-table-column-start]")
    ).filter((element) => {
      const start = Number(element.dataset.tableColumnStart);
      const end = Number(element.dataset.tableColumnEnd);
      // A merged cell already has the width of several grid columns; it must
      // not force the last of those columns to grow on its own.
      return start === index && end === index;
    });
    const requiredWidth = Math.max(
      32,
      ...matchingCells.map((element) => this.tableMeasureNaturalCellWidth(element))
    );
    const requestedWidth = Math.max(5, Math.min(95, (requiredWidth / tableWidth) * 100));
    const neighbor = index < widths.length - 1 ? index + 1 : index - 1;
    const requestedDelta = requestedWidth - widths[index];
    const delta = Math.max(
      5 - widths[index],
      Math.min(requestedDelta, widths[neighbor] - 5)
    );
    widths[index] += delta;
    widths[neighbor] -= delta;
    this.setState(
      { currentBlock: info.currentBlock, tableResizePreview: null },
      () =>
        this.tableSave(tableStructure.applyColumnWidths(syncedTable, widths), {
          preserveExistingBlockFormatting: true,
        })
    );
  };

  tableAutoFitCurrent = (axis) => {
    const currentBlock = this.state.currentBlock;
    if (!currentBlock || !currentBlock.key) return;
    this.onTableAutoFit(axis, currentBlock.key);
  };

  tableApplyBorderPreset = (preset) => {
    const tableInfo = this.tableGetCurrentInfo();
    if (!tableInfo) return;
    const selectedKeys = this.state.selectedCells.length
      ? this.state.selectedCells
      : [this.state.currentBlock.key];
    const table = tableStructure.applyBorderPreset(
      tableInfo.table,
      selectedKeys,
      preset
    );
    this.tableSave(table, { preserveExistingBlockFormatting: true });
  };

  renderTableResizeGuide = () => {
    const preview = this.state.tableResizePreview;
    if (!preview || !preview.guide) return null;
    const { axis, left, top, length } = preview.guide;
    return (
      <div
        className={`table-resize-guide table-resize-guide--${axis}`}
        aria-hidden="true"
        style={
          axis === "column"
            ? { left: `${left}px`, top: `${top}px`, height: `${length}px` }
            : { left: `${left}px`, top: `${top}px`, width: `${length}px` }
        }
      />
    );
  };

  renderTableContextMenu = () => {
    const menu = this.state.tableContextMenu;
    if (!menu || this.props.readOnly) return null;
    const groups = [
      {
        id: "selection",
        label: "Выделение",
        icon: "fa-object-group",
        open: true,
        items: [
          ["table-select-row", "Выделить всю строку", "fa-arrows-h"],
          ["table-select-col", "Выделить весь столбец", "fa-arrows-v"],
        ],
      },
      {
        id: "size",
        label: "Размер",
        icon: "fa-arrows",
        items: [
          ["table-autofit-col", "Подобрать ширину по тексту", "fa-arrows-h"],
          ["table-autofit-row", "Подобрать высоту по тексту", "fa-arrows-v"],
        ],
      },
      {
        id: "borders",
        label: "Границы",
        icon: "fa-th",
        items: [
          ["table-border-settings", "Настроить границы…", "fa-table"],
          ["table-border-all", "Все границы", "fa-th"],
          ["table-border-none", "Нет границ", "fa-square-o"],
          ["table-border-outer", "Внешние границы", "fa-square"],
          ["table-border-inner", "Внутренние границы", "fa-th"],
          ["table-border-inner-horizontal", "Внутренние горизонтальные", "fa-bars"],
          ["table-border-inner-vertical", "Внутренние вертикальные", "fa-columns"],
        ],
      },
      {
        id: "structure",
        label: "Структура таблицы",
        icon: "fa-table",
        items: [
          ["table-merge-cells", "Объединить ячейки", "fa-compress"],
          ["table-split-cell", "Разделить ячейку", "fa-expand"],
          ["table-add-up", "Добавить строку выше", "fa-plus"],
          ["table-add-down", "Добавить строку ниже", "fa-plus"],
          ["table-add-left", "Добавить столбец слева", "fa-plus"],
          ["table-add-right", "Добавить столбец справа", "fa-plus"],
          ["table-remove-row", "Удалить строку", "fa-minus", true],
          ["table-remove-col", "Удалить столбец", "fa-minus", true],
          ["table-remove", "Удалить таблицу", "fa-times", true],
        ],
      },
    ];
    return (
      <div
        className="table-context-menu"
        role="menu"
        aria-label="Действия с таблицей"
        style={{ left: menu.left, top: menu.top, maxHeight: `${menu.maxHeight}px` }}
        onContextMenu={(event) => event.preventDefault()}
      >
        {groups.map((group) => (
          <details key={group.id} className="table-context-menu__group" open={group.open}>
            <summary>
              <span className={`fa ${group.icon}`} aria-hidden="true" />
              {group.label}
            </summary>
            <div className="table-context-menu__items">
              {group.items.map((item) => (
                <button
                  type="button"
                  role="menuitem"
                  key={item[0]}
                  className={item[3] ? "is-danger" : ""}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => this.onTableContextMenuAction(item[0])}
                >
                  <span className={`fa ${item[2]}`} aria-hidden="true" />
                  {item[1]}
                </button>
              ))}
            </div>
          </details>
        ))}
      </div>
    );
  };

  onRulerHandleMouseDown = (kind, event) => {
    if (this.props.readOnly || !this.horizontalRulerRef.current) return;

    event.preventDefault();
    event.stopPropagation();
    const data = this.state.currentBlock.data || {};
    const values = {
      paddingLeft: Math.max(0, parseInt(data.paddingLeft, 10) || 0),
      paddingRight: Math.max(0, parseInt(data.paddingRight, 10) || 0),
      firstLineIndent: Math.max(0, parseInt(data.firstLineIndent, 10) || 0),
    };
    this.rulerDragValues = values;
    this.setState({ rulerDrag: { ...values, kind } });
  };

  syncRulerStickyOffset = () => {
    const ruler = this.horizontalRulerRef.current;
    const toolbar = document.querySelector(".editor-toolbar");
    if (!ruler || !toolbar) return;

    // The app header may collapse while the document scrolls. Measure the
    // visible toolbar each time instead of using a fixed viewport offset.
    ruler.style.setProperty(
      "--editor-ruler-sticky-top",
      `${Math.max(0, toolbar.getBoundingClientRect().bottom)}px`
    );
  };

  onRulerDragMove = (event) => {
    const drag = this.state.rulerDrag;
    const ruler = this.horizontalRulerRef.current;
    if (!drag || !ruler || event.buttons !== 1) return;

    const rect = ruler.getBoundingClientRect();
    const scale = Number(this.state.currentScale || 100) / 100;
    const maxIndent = this.getRulerMaxIndent();
    const valueAtPointer = Math.round(
      Math.max(0, Math.min(rect.width, event.clientX - rect.left)) / (16 * scale)
    );
    const values = { ...this.rulerDragValues };

    if (drag.kind === "paddingRight") {
      values.paddingRight = Math.max(
        0,
        Math.min(maxIndent, Math.round((rect.right - event.clientX) / (16 * scale)))
      );
    } else if (drag.kind === "firstLineIndent") {
      values.firstLineIndent = Math.max(
        0,
        Math.min(maxIndent - values.paddingLeft, valueAtPointer - values.paddingLeft)
      );
    } else {
      values.paddingLeft = Math.max(0, Math.min(maxIndent, valueAtPointer));
      values.firstLineIndent = Math.min(
        values.firstLineIndent,
        maxIndent - values.paddingLeft
      );
    }

    this.rulerDragValues = values;
    this.setState({ rulerDrag: { ...values, kind: drag.kind } });
  };

  onRulerDragEnd = () => {
    const drag = this.state.rulerDrag;
    if (!drag || !this.rulerDragValues) return;

    const values = this.rulerDragValues;
    this.rulerDragValues = null;
    this.setState({ rulerDrag: null });

    let editorState = this.state.editorState;
    const contentState = Modifier.mergeBlockData(
      editorState.getCurrentContent(),
      editorState.getSelection(),
      values
    );
    editorState = EditorState.push(editorState, contentState, "change-block-data");
    if (editorState !== this.state.editorState) this.onChange(editorState);
  };

  getRulerMaxIndent = () =>
    Math.floor((this.state.editorWidth + 60) / 16);

  renderDocumentRulers = () => {
    const scale = Number(this.state.currentScale || 100) / 100;
    const pageWidth = this.state.editorWidth + 60;
    const pageHeight =
      this.state.pageSettings.orientation === "landscape" ? 767 : 1420;
    const horizontalMarks = Array.from({ length: 22 }, (_, value) => value);
    const verticalMarks = Array.from({ length: 30 }, (_, value) => value);
    const verticalLeft = pageWidth * scale / 2 + 30;
    const blockData = this.state.currentBlock.data || {};
    const dragValues = this.state.rulerDrag || blockData;
    const paddingLeft = Math.max(0, parseInt(dragValues.paddingLeft, 10) || 0);
    const paddingRight = Math.max(0, parseInt(dragValues.paddingRight, 10) || 0);
    const firstLineIndent = Math.max(
      0,
      parseInt(dragValues.firstLineIndent, 10) || 0
    );
    const indentUnit = 16;

    return (
      <>
        <div
          className="editor-ruler editor-ruler--horizontal"
          ref={this.horizontalRulerRef}
          role="group"
          aria-label="Линейка отступов абзаца"
          style={{
            width: `${pageWidth}px`,
            transform: `scaleX(${scale})`,
          }}
        >
          {horizontalMarks.map((mark) => (
            <span
              className="editor-ruler__mark"
              aria-hidden="true"
              key={`horizontal-${mark}`}
              style={{ left: `${(mark / (horizontalMarks.length - 1)) * 100}%` }}
            >
              {mark}
            </span>
          ))}
          <button
            type="button"
            className="editor-ruler__handle editor-ruler__handle--first-line"
            style={{ left: `${(paddingLeft + firstLineIndent) * indentUnit}px` }}
            title="Красная строка: перетащите, чтобы изменить отступ первой строки"
            aria-label="Красная строка"
            onMouseDown={(event) => this.onRulerHandleMouseDown("firstLineIndent", event)}
          />
          <button
            type="button"
            className="editor-ruler__handle editor-ruler__handle--left-indent"
            style={{ left: `${paddingLeft * indentUnit}px` }}
            title="Левый отступ: перетащите, чтобы изменить отступ абзаца"
            aria-label="Левый отступ"
            onMouseDown={(event) => this.onRulerHandleMouseDown("paddingLeft", event)}
          />
          <button
            type="button"
            className="editor-ruler__handle editor-ruler__handle--right-indent"
            style={{ right: `${paddingRight * indentUnit}px` }}
            title="Правый отступ: перетащите, чтобы изменить отступ абзаца"
            aria-label="Правый отступ"
            onMouseDown={(event) => this.onRulerHandleMouseDown("paddingRight", event)}
          />
        </div>
        <div className="editor-rulers no-print" aria-hidden="true">
          <div
            className="editor-ruler editor-ruler--vertical"
            style={{
              height: `${pageHeight}px`,
              left: `calc(50% - ${verticalLeft}px)`,
              transform: `scaleY(${scale})`,
            }}
          >
            {verticalMarks.map((mark) => (
              <span
                className="editor-ruler__mark"
                key={`vertical-${mark}`}
                style={{ top: `${(mark / (verticalMarks.length - 1)) * 100}%` }}
              >
                {mark}
              </span>
            ))}
          </div>
        </div>
      </>
    );
  };

  //Change entire scale of a document (#edior)
  //& adjust container height
  onToggleScale = (scale) => {
    if (scale === "params") {
      this.setState({ showPageDialog: true });
      return;
    }
    this.setState({ currentScale: scale }, () => {
      this.adjustEditorHeight();
    });
  };
  adjustEditorHeight = () => {
    if (this.editorRef.current) {
      let height = this.editorRef.current.offsetHeight;
      height = (height * parseInt(this.state.currentScale)) / 100 + 60;
      this.setState({ currentEditorHeight: `${height}px` });
    }
  };

  /* ------ Handling keys And Paste ------ */

  keyGetHotkeys = () => {
    let result = [
      {
        ctrlKey: false,
        shiftKey: false,
        altKey: true,
        keyCode: 80, //Alt+P
        callback: () => this.toggleBlockType("paragraph"),
      },
      {
        ctrlKey: false,
        shiftKey: false,
        altKey: true,
        keyCode: 84, //Alt+T
        callback: () => this.toggleBlockType("table"),
      },
      {
        ctrlKey: true,
        shiftKey: false,
        altKey: false,
        keyCode: 66, //Ctrl+B
        callback: () => this.toggleInlineStyle("BOLD"),
      },
      {
        ctrlKey: true,
        shiftKey: false,
        altKey: false,
        keyCode: 73, //Ctrl+I
        callback: () => this.toggleInlineStyle("ITALIC"),
      },
      {
        ctrlKey: true,
        shiftKey: false,
        altKey: false,
        keyCode: 85, //Ctrl+U
        callback: () => this.toggleInlineStyle("UNDERLINE"),
      },
      {
        ctrlKey: false,
        shiftKey: true,
        altKey: true,
        keyCode: 107, //Shift+Alt+ +
        callback: () => this.tableColResize(1),
      },
      {
        ctrlKey: false,
        shiftKey: true,
        altKey: true,
        keyCode: 109, //Shift+Alt+ -
        callback: () => this.tableColResize(-1),
      },
      {
        ctrlKey: false,
        shiftKey: true,
        altKey: true,
        keyCode: 39, //Shift+Alt+ ->
        callback: () => this.toggleBlockType("indent"),
      },
      {
        ctrlKey: false,
        shiftKey: true,
        altKey: true,
        keyCode: 37, //Shift+Alt+ <-
        callback: () => this.toggleBlockType("outdent"),
      },
    ];

    for (let k = 0; k < 9; k++) {
      result.push({
        ctrlKey: true,
        shiftKey: false,
        altKey: true,
        keyCode: 49 + k, //Ctrl+Alt+...n...
        callback: () => this.togglePreset(PRESETS[k]),
      });
      result.push({
        ctrlKey: false,
        shiftKey: true,
        altKey: true,
        keyCode: 49 + k, //Shift+Alt+...n...
        callback: () => this.toggleBlockType(BLOCKS[k]),
      });
    }

    return result;
  };

  keyBindingFn = (event) => {
    if ((event.ctrlKey || event.metaKey) && !event.shiftKey && !event.altKey) {
      if (event.keyCode === 70 || event.keyCode === 72) {
        event.preventDefault();
        return "find-replace";
      }
    }
    if ((event.ctrlKey || event.metaKey) && event.shiftKey && !event.altKey) {
      if (event.keyCode === 67) {
        event.preventDefault();
        return "format-painter-copy";
      }
      if (event.keyCode === 86) {
        event.preventDefault();
        return "format-painter-apply";
      }
    }
    if (event.key === "Escape" && this.state.formatPainter) {
      return "format-painter-cancel";
    }

    const { editorState, currentBlock } = this.state;
    const selection = editorState.getSelection();
    const selectionAnchorKey = selection.getAnchorKey();
    const selectionFocusKey = selection.getFocusKey();

    const { key, type } = currentBlock;
    const content = editorState.getCurrentContent();

    const hotKeys = this.keyGetHotkeys();
    hotKeys.forEach((key) => {
      if (
        event.shiftKey === key.shiftKey &&
        event.ctrlKey === key.ctrlKey &&
        event.altKey === key.altKey
      ) {
        if (event.keyCode === key.keyCode) {
          key.callback();
        }
      }
    });

    if (!selection.isCollapsed()) {
      if (selectionAnchorKey !== selectionFocusKey) {
        if (
          this.state.selectedBlocksTypes.includes("table") ||
          this.state.selectedBlocksTypes.includes("cell")
        ) {
          //If selection is crossing table block  we need other procedure
          if (event.key === "Delete" || event.key === "Backspace") {
            this.tableDeleteHandle();
            return false;
          }

          return false;
        }
      }
    } else {
      if (type === "cell" || type === "table") {
        if (event.key === "Backspace") {
          if (selection.getAnchorOffset() === 0) {
            return false;
          }
        }
        if (event.key === "Delete") {
          if (selection.getFocusOffset() === currentBlock.length) {
            return false;
          }
        }
      }
    }

    if (event.key === "Tab") {
      if (type === "cell" || type === "table") {
        const blockAfter = content.getBlockAfter(key);
        if (blockAfter) {
          const blockAfterKey = blockAfter.getKey();
          this.blockForceFocus(blockAfterKey);
        } else {
          this.blockInsertNew({
            type: "unstyled",
            text: "",
            characterList: List(),
          });
        }
        return false;
      }

      if (type === "grid") {
        const blockAfter = content.getBlockAfter(key);
        if (blockAfter) {
          const blockAfterType = blockAfter.getType();
          const blockAfterKey = blockAfter.getKey();

          if (blockAfterType === "grid") {
            this.blockForceFocus(blockAfterKey);
            return false;
          } else {
            this.blockInsertNew(
              {
                type: "grid",
                text: "",
                characterList: List(),
              },
              blockAfterKey
            );
          }
        } else {
          this.blockInsertNew({
            type: "grid",
            text: "",
            characterList: List(),
          });
        }
      } else {
        this.onChange(RichUtils.onTab(event, editorState, 3));
      }
      return false;
    }

    return getDefaultKeyBinding(event);
  };

  handleReturn = (event) => {
    const { editorState, currentBlock } = this.state;
    if (isSoftNewlineEvent(event)) {
      this.onChange(RichUtils.insertSoftNewline(editorState));
      return "handled";
    }
    const { type } = currentBlock;
    if (type === "grid" || type === "table" || type === "cell") {
      this.onChange(RichUtils.insertSoftNewline(editorState));
      return "handled";
    }

    this.insertNewlineWithInheritedBlockData();
    return "handled";
  };

  insertNewlineWithInheritedBlockData = () => {
    const { editorState } = this.state;
    const selection = editorState.getSelection();
    const contentState = editorState.getCurrentContent();
    const sourceBlock = contentState.getBlockForKey(selection.getStartKey());
    let nextContentState = Modifier.splitBlock(contentState, selection);
    const nextSelection = nextContentState.getSelectionAfter();

    // Draft.js creates the second block with empty data. Keep paragraph-level
    // settings (including ruler indents) when Enter creates a new paragraph.
    if (sourceBlock && nextSelection.getStartKey() !== sourceBlock.getKey()) {
      nextContentState = Modifier.mergeBlockData(
        nextContentState,
        SelectionState.createEmpty(nextSelection.getStartKey()),
        sourceBlock.getData()
      );
    }

    this.onChange(
      EditorState.forceSelection(
        EditorState.push(editorState, nextContentState, "split-block"),
        nextSelection
      )
    );
  };

  handleWindowPaste = (e) => {
    if (e.clipboardData) {
      var items = e.clipboardData.items;
      if (items) {
        for (var i = 0; i < items.length; i++) {
          if (items[i].type.indexOf("image") !== -1) {
            this.handleDroppedImage(items[i].getAsFile());
          }
        }
      }
    }
  };

  handlePastedText = (text, html, editorState) => {
    if (this.editorComponentRef.current) {
      const fragment = this.editorComponentRef.current.getClipboard();

      if (fragment) {
        let entitiesInFragment = this.entitiesInFragment(fragment);

        let finalFragment = null;

        if (entitiesInFragment.length > 0) {
          if (entitiesInFragment.length === 1 && text.trim() === "") {
            this.entityPaste(entitiesInFragment[0]);
            return true;
          } else {
            const fragmentUnstyled = this.blockClearTypesInFragment(fragment);
            finalFragment = this.entityClearFromFragment(fragmentUnstyled);
          }
        } else {
          finalFragment = this.blockClearTypesInFragment(fragment);
        }

        let newContent = Modifier.replaceWithFragment(
          editorState.getCurrentContent(),
          editorState.getSelection(),
          finalFragment
        );
        this.onChange(
          EditorState.push(editorState, newContent, "insert-fragment"),
          () => {
            let currBlockData = this.blockDataCollect();
            this.blockDataRestore(currBlockData);
          }
        );

        this.editorComponentRef.current.setClipboard(null);
        return true;
      } else if (html) {
        const sanitizedHTML = sanitizePastedHTML(html);
        const content = sanitizedHTML ? fromHTML(sanitizedHTML) : null;
        const finalFragment = content
          ? this.wordTablesToFragment(content.getBlockMap())
          : null;

        if (finalFragment && finalFragment.size) {
          const newContent = Modifier.replaceWithFragment(
            editorState.getCurrentContent(),
            editorState.getSelection(),
            finalFragment
          );
          this.onChange(
            EditorState.push(editorState, newContent, "insert-fragment"),
            () => {
              const currBlockData = this.blockDataCollect();
              this.blockDataRestore(currBlockData);
            }
          );
          return true;
        }
      }

      if (text) {
        const finalFragment = ContentState.createFromText(text).blockMap;
        let newContent = Modifier.replaceWithFragment(
          editorState.getCurrentContent(),
          editorState.getSelection(),
          finalFragment
        );
        this.onChange(
          EditorState.push(editorState, newContent, "insert-fragment"),
          () => {
            let currBlockData = this.blockDataCollect();
            this.blockDataRestore(currBlockData);
          }
        );
        return true;
      }
    }
  };

  wordTablesToFragment = (fragment) => {
    const blocks = [];
    fragment.forEach((block) => {
      if (block.getType() !== "word-table") {
        blocks.push([block.getKey(), block]);
        return;
      }

      const sourceTable = block.getData().get("wordTable");
      const columnCount = Math.max(
        0,
        ...sourceTable.map((row) => row.length)
      );
      if (!columnCount) return;

      const cellDefaults = tableStructure.cellDefaults();
      const table = sourceTable.map((sourceRow, rowIndex) =>
        Array.from({ length: columnCount }, (_, columnIndex) => {
          const sourceCell = sourceRow[columnIndex] || { text: "" };
          return tableStructure.makeCell({
            ...cellDefaults,
            borderTop: true,
            borderRight: true,
            borderBottom: true,
            borderLeft: true,
            width: 100 / columnCount,
            text: sourceCell.text || "",
            self: rowIndex === 0 && columnIndex === 0,
          });
        })
      );

      const rootKey = table[0][0].key;
      table.forEach((row) => {
        row.forEach((cell) => {
          blocks.push([
            cell.key,
            new ContentBlock({
              key: cell.key,
              type: cell.self ? "table" : "cell",
              text: cell.text,
              data: new Map({
                table: cell.self ? table : null,
                rootKey: cell.self ? null : rootKey,
                ...cell,
              }),
            }),
          ]);
        });
      });
    });
    return OrderedMap(blocks);
  };

  handleDroppedFiles = (selection, files) => {
    let file = files[0];
    if (file.type.startsWith("image/")) {
      this.handleDroppedImage(file);
    }
    return "handled";
  };

  handleDroppedImage = (file) => {
    if (file) {
      let reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onloadend = () => {
        this.setState({ pending: false });
      };
      reader.onloadstart = () => {
        this.setState({ pending: true });
      };
      reader.onload = () => {
        compressor(reader.result).then((v) => {
          this.entityInsert("USER_IMAGE", "SEGMENTED", {
            image: v,
            file,
            kind: "image",
            float: "none",
          });
        });
      };
      reader.onerror = () => {
        this.context.toast(reader.error, "error");
      };
    }
  };

  insertFragment = (editorState, fragment) => {
    let newContent = Modifier.replaceWithFragment(
      editorState.getCurrentContent(),
      editorState.getSelection(),
      fragment
    );
    return EditorState.push(editorState, newContent, "insert-fragment");
  };

  /* ------ Entities ------ */

  entityInsertInline = (...entityParams) => {
    let { editorState } = this.state;
    let selection = editorState.getSelection();
    let content = editorState.getCurrentContent();
    if (selection.isCollapsed()) {
      this.context.toast(
        "Выберите фрагмент текста, чтобы вставить ссылку",
        "error"
      );
    } else {
      content = content.createEntity(...entityParams);
      content = Modifier.applyEntity(
        content,
        selection,
        content.getLastCreatedEntityKey()
      );
      this.onChange(
        EditorState.set(editorState, { currentContent: content, selection })
      );
    }
  };

  //Inserts or replaces entity on current selection
  entityInsert = (...entityParams) => {
    let { editorState } = this.state;
    let selection = editorState.getSelection();
    let content = editorState.getCurrentContent();

    content = content.createEntity(...entityParams);
    let modifierFn = selection.isCollapsed()
      ? Modifier.insertText
      : Modifier.replaceText;

    content = modifierFn(
      content,
      selection,
      " ",
      null,
      content.getLastCreatedEntityKey()
    );
    selection = selection.merge({
      anchorOffset: selection.anchorOffset + 1,
      focusOffset: selection.anchorOffset + 1,
    });
    content = Modifier.insertText(content, selection, " ");

    this.onChange(
      EditorState.set(editorState, { currentContent: content, selection })
    );
  };

  //Make copy of entity data, then insert new entity in current
  //selection with copied data, but no name and code
  entityPaste = (entityKey) => {
    const content = this.state.editorState.getCurrentContent();
    const entity = content.getEntity(entityKey);
    const entityData = entity.getData();
    const entityType = entity.getType();
    let entityDataRaw = {};
    for (let dataKey in entityData) {
      let value = entityData[dataKey];
      if (dataKey === "list" && Array.isArray(entityData.list)) {
        value = [];
        entityData.list.forEach((item) => {
          value.push(Object.assign({}, item));
        });
      }
      entityDataRaw[dataKey] = value;
    }
    entityDataRaw.name = "";
    entityDataRaw.code = this.context.key();

    this.entityInsert(entityType, "SEGMENTED", entityDataRaw);
  };

  //Clears all entities from provided fragment
  entityClearFromFragment = (fragment) => {
    let newFragment = blockMapBuid.createFromArray([]);
    fragment.forEach((block, blockKey) => {
      let updatedBlock = block;
      block.findEntityRanges(
        (character) => character.getEntity() !== null,
        (start, end) => {
          updatedBlock = this.entityApplyToContentBlock(
            updatedBlock,
            start,
            end,
            null
          );
          newFragment = newFragment.set(blockKey, updatedBlock);
        }
      );
    });

    return newFragment;
  };

  //Service needs...
  entityApplyToContentBlock = (contentBlock, start, end, entityKey) => {
    var characterList = contentBlock.getCharacterList();
    while (start < end) {
      characterList = characterList.set(
        start,
        CharacterMetadata.applyEntity(characterList.get(start), entityKey)
      );
      start++;
    }
    return contentBlock.set("characterList", characterList);
  };

  //Returns all entity keys from provided fagment
  entitiesInFragment = (fragment) => {
    let entities = [];
    fragment.forEach((block) => {
      block.getCharacterList().forEach((character) => {
        const key = character.getEntity();
        if (key !== null) {
          entities.push(key);
        }
      });
    });

    return entities;
  };

  //Return entity's selection range by entityKey
  entityGetRange = (entityKey) => {
    let entityRange = {};
    if (entityKey) {
      let content = this.state.editorState.getCurrentContent();
      content.getBlocksAsArray().forEach((block) => {
        block.findEntityRanges(
          (value) => value.get("entity") === entityKey,
          (start, end) => {
            entityRange = {
              start,
              end,
              block: block.getKey(),
            };
          }
        );
      });
    }
    return entityRange;
  };

  //Return all the entities in content
  entityGetAll = (entityType = null) => {
    const content = this.state.editorState.getCurrentContent();
    const entities = [];
    content.getBlocksAsArray().forEach((block) => {
      let selectedEntity = null;
      block.findEntityRanges(
        (character) => {
          if (character.getEntity() !== null) {
            const entity = content.getEntity(character.getEntity());
            if (
              !entityType ||
              (entityType && entity.getType() === entityType)
            ) {
              selectedEntity = {
                entityKey: character.getEntity(),
                block: block.getKey(),
                entity: content.getEntity(character.getEntity()),
              };
              return true;
            }
          }
          return false;
        },
        (start, end) => {
          entities.push({ ...selectedEntity, start, end });
        }
      );
    });
    return entities;
  };

  //Fixes entity text in the USER_INPUT entities after paste
  entityFixText = () => {
    const { editorState } = this.state;
    let content = editorState.getCurrentContent();
    let lastSelection = editorState.getSelection();
    let entities = this.entityGetAll("USER_INPUT");
    entities.forEach((entity) => {
      let selection = SelectionState.createEmpty(entity.block);
      selection = selection.merge({
        anchorKey: entity.block,
        focusKey: entity.block,
        anchorOffset: entity.start,
        focusOffset: entity.end,
      });
      content = Modifier.replaceText(
        content,
        selection,
        " ",
        null,
        entity.entityKey
      );
    });
    this.onChange(
      EditorState.set(editorState, {
        currentContent: content,
        selection: lastSelection,
      })
    );
  };

  /* -----  Editor Click Handle ------ */

  editorDblClick = (e) => {
    let entityFound = false;
    if (e.target) {
      let entityKey = e.target.dataset.entity || null;
      if (entityKey) {
        entityFound = true;
        this.editorEntityDblClick(entityKey, e);
      } else {
        if (e.target.dataset.text === "true") {
          entityKey = e.target.parentElement.parentElement.dataset.entity;
          if (entityKey) {
            entityFound = true;
            this.editorEntityDblClick(entityKey, e);
          }
        }
      }
    }
  };

  editorEntityDblClick = (entityKey, e) => {
    this.setState({
      selectedEntity: entityKey,
      selectedEntityElem: e.target,
      showEntityDialog: true,
    });
  };

  editorEntityClick = (entityKey) => {
    this.highlightEntity(entityKey);
    let entityRange = this.entityGetRange(entityKey);
    if (entityRange.block) {
      let selection = SelectionState.createEmpty(entityRange.block);
      selection = selection.merge({
        anchorKey: entityRange.block,
        focusKey: entityRange.block,
        anchorOffset: entityRange.start,
        focusOffset: entityRange.end,
      });

      this.onChange(
        EditorState.forceSelection(this.state.editorState, selection)
      );
    }
  };

  editorClick = (e) => {
    if (this.suppressNextTableClick) {
      this.suppressNextTableClick = false;
      return;
    }
    const { key, type } = this.state.currentBlock;

    //Table cells selection handle
    let selectedCells = this.state.selectedCells;
    if (type !== "cell" && type !== "table") {
      selectedCells = [];
    } else {
      if (e.ctrlKey) {
        selectedCells = [...new Set([...selectedCells, key])];
      } else {
        selectedCells = [key];
      }
    }
    this.setState({ selectedCells }, () => this.highlightCells());

    if (e.target) {
      let entityKey = e.target.dataset.entity || null;

      if (entityKey) {
        this.editorEntityClick(entityKey);
      } else {
        if (this.state.editorFocused) this.highlightEntity(null);
      }

      if (this.editorComponentRef.current)
        this.editorComponentRef.current.focus();
    }
  };

  /* -------  Dialogs Handling ------ */

  onEntityDialogHide = () => {
    this.setState({ showEntityDialog: false });
    this.editorClick({ target: this.state.selectedEntityElem });
  };

  onEntityDialogSave = (entityKey, entityData) => {
    let { editorState } = this.state;
    let content = editorState.getCurrentContent();
    content.mergeEntityData(entityKey, entityData);
    this.onChange(EditorState.set(editorState, { currentContent: content }));
    this.onEntityDialogHide();
  };

  onTableDialogShow = () => {
    let table = this.tableGetForBlock(this.state.currentBlock);
    if (table && Array.isArray(table)) {
      this.setState({ showTableDialog: true, currentTable: table });
    }
  };

  onTableDialogHide = () => {
    const offset = window.pageYOffset;
    this.blockMergeData(this.state.currentBlock.key, {});
    this.setState({ showTableDialog: false, currentTable: false }, () =>
      window.scrollTo(0, offset)
    );
  };

  onTableDialogSave = (resut) => {
    const offset = window.pageYOffset;
    let table = resut.table.map((row) => row.map((cell) => ({ ...cell })));
    const selectedKeys = this.state.selectedCells.length
      ? this.state.selectedCells
      : [this.state.currentBlock.key];
    if (resut.borderPreset) {
      table = tableStructure.applyBorderPreset(
        table,
        selectedKeys,
        resut.borderPreset
      );
    } else {
      selectedKeys.forEach((key) => {
        const { row, col } = tableStructure.cellLocate(table, key);
        if (table[row] && table[row][col]) {
          table[row][col] = { ...table[row][col], ...resut.data };
        }
      });
    }

    this.tableSave(table);
    this.setState({ showTableDialog: false, currentTable: false }, () =>
      window.scrollTo(0, offset)
    );
  };

  onPageDialogHide = () => {
    const offset = window.pageYOffset;
    this.setState({ showPageDialog: false }, () => window.scrollTo(0, offset));
  };

  onPageDialogSave = (pageSettings) => {
    const offset = window.pageYOffset;
    this.setState({ pageSettings, showPageDialog: false }, () => {
      this.applyPageSettings();
      window.scrollTo(0, offset);
    });
  };

  onParagraphDialogHide = () => {
    const offset = window.pageYOffset;
    this.blockMergeData(this.state.currentBlock.key, {});
    this.setState({ showParagraphDialog: false }, () => {
      window.scrollTo(0, offset);
    });
  };

  onParagraphDialogSave = (blockData) => {
    const { selectedBlocks, selectedCells } = this.state;
    let selectedKeys = [];
    const offset = window.pageYOffset;

    this.blocksFewMergeData(
      [...new Set(selectedKeys.concat(selectedBlocks, selectedCells))],
      blockData,
      this.state.editorState.getSelection()
    );
    this.setState({ showParagraphDialog: false }, () =>
      window.scrollTo(0, offset)
    );
  };

  // ------ Blocks ------ //
  blockStyleFn = (contentBlock) => {
    const type = contentBlock.getType();
    const data = contentBlock.getData().toJS() || {};

    let classes = ["editor-block", `editor-block-${type}`];

    if (data.align && type !== "table" && type !== "cell") {
      classes.push(`editor-block-align-${data.align}`);
    }

    for (let k in data) {
      if (type !== "table") {
        classes.push(`editor-block-data-${k}-${data[k]}`);
      }
    }

    return classes.join(" ");
  };
  blockRenderFn = (contentBlock) => {
    const type = contentBlock.getType();
    if (type === "grid") {
      let { height } = blockTableTools.blockFindInTables(
        contentBlock.getKey(),
        this.state.blockTables
      );
      return {
        component: EditorGridCell,
        editable: true,
        props: {
          onResize: this.blockOnResize,
          onContentResize: this.blockOnContentResize,
          forceHeight: height || null,
          editorWidth: this.state.editorWidth,
          columnWidth: this.state.columnWidth,
          defaultHeight: 48,
        },
      };
    }

    if (type === "table") {
      return {
        component: TableBlock,
        editable: true,
        props: {
          onTableContextMenu: this.props.readOnly
            ? null
            : this.onTableContextMenu,
          onTableCellMouseDown: this.props.readOnly
            ? null
            : this.onTableCellMouseDown,
          onTableCellMouseEnter: this.props.readOnly
            ? null
            : this.onTableCellMouseEnter,
          onTableResizeStart: this.props.readOnly
            ? null
            : this.onTableResizeStart,
          onTableAutoFit: this.props.readOnly ? null : this.onTableAutoFit,
          tableResizePreview: this.state.tableResizePreview,
        },
      };
    }
    if (type === "cell") {
      return {
        component: TableCell,
        editable: true,
        props: {
          onTableContextMenu: this.props.readOnly
            ? null
            : this.onTableContextMenu,
          onTableCellMouseDown: this.props.readOnly
            ? null
            : this.onTableCellMouseDown,
          onTableCellMouseEnter: this.props.readOnly
            ? null
            : this.onTableCellMouseEnter,
        },
      };
    }
  };
  blockIndent = () => {
    const data = this.state.currentBlock.data || {};
    const initialIndent = parseInt(data.paddingLeft) || 0;
    if (initialIndent >= this.getRulerMaxIndent()) return;
    this.blockIndentChange(initialIndent + 1);
  };
  blockOutdent = () => {
    const data = this.state.currentBlock.data || {};
    const initialIndent = parseInt(data.paddingLeft) || 0;
    if (initialIndent < 1) return;
    this.blockIndentChange(initialIndent - 1);
  };
  blockIndentChange = (paddingLeft) => {
    let editorState = this.state.editorState;
    const newContentState = Modifier.mergeBlockData(
      editorState.getCurrentContent(),
      editorState.getSelection(),
      { paddingLeft }
    );
    editorState = EditorState.push(
      editorState,
      newContentState,
      "change-block-data"
    );
    if (editorState !== this.state.editorState) this.onChange(editorState);
  };

  blocksFewMergeData = (blockKeys = [], data = {}, prevSelection = false) => {
    let editorState = this.state.editorState;
    let contentState = editorState.getCurrentContent();
    blockKeys.forEach((blockKey) => {
      contentState = Modifier.mergeBlockData(
        contentState,
        SelectionState.createEmpty(blockKey),
        data
      );
    });
    editorState = EditorState.push(editorState, contentState, "block-data");
    if (prevSelection) {
      editorState = EditorState.forceSelection(editorState, prevSelection);
    }

    this.onChange(editorState);
  };

  blockMergeData = (blockKey = false, data = {}) => {
    const currentSelection = this.state.editorState.getSelection();
    const blockSelection = blockKey
      ? SelectionState.createEmpty(blockKey)
      : currentSelection;
    this.onChange(
      EditorState.push(
        this.state.editorState,
        Modifier.mergeBlockData(
          this.state.editorState.getCurrentContent(),
          blockSelection,
          data
        ),
        "block-data"
      ),
      () => {
        this.onChange(
          EditorState.forceSelection(this.state.editorState, currentSelection)
        );
      }
    );
  };
  blockOnResize = (blockKey, w, h) => {
    this.blockMergeData(blockKey, { width: w, height: h });
  };
  blockOnContentResize = (blockKey, w, h, heightAdjust) => {
    let newData = { contentWidth: w, contentHeight: h };
    if (heightAdjust) newData.height = heightAdjust;
    this.blockMergeData(blockKey, newData);
  };
  blockForceFocus = (blockKey) => {
    if (!blockKey) return;
    const { editorState } = this.state;
    const contentState = editorState.getCurrentContent();

    const block = contentState.getBlockForKey(blockKey);
    if (block) {
      const type = block.getType();
      if (type === "table" || type === "cell") {
        this.setState({ selectedCells: [blockKey] }, () =>
          this.highlightCells()
        );
      }
    }

    const newSelection = SelectionState.createEmpty(blockKey);
    this.onChange(EditorState.forceSelection(editorState, newSelection));
  };
  blockCreateBlank = () => {
    const newBlockKey = genKey();
    return new ContentBlock({
      key: newBlockKey,
      type: "unstyled",
      text: "",
      characterList: List(),
    });
  };
  blockInsertNew = (blockData = {}, afterKey = false, callbackFn = false) => {
    const { editorState } = this.state;
    const contentState = editorState.getCurrentContent();
    const blockMap = contentState.getBlockMap();
    const newBlockKey = genKey();
    const newBlock = new ContentBlock({
      key: newBlockKey,
      ...blockData,
    });

    let newBlockMap = null;

    if (afterKey) {
      newBlockMap = OrderedMap().withMutations((map) => {
        for (let [k, v] of blockMap.entries()) {
          if (afterKey === k) {
            map.set(newBlock.key, newBlock);
          }
          map.set(k, v);
        }
      });
    } else {
      newBlockMap = blockMap
        .toSeq()
        .concat([[newBlock.getKey(), newBlock]])
        .toOrderedMap();
    }

    const newContent = contentState.merge({
      blockMap: newBlockMap,
    });

    this.onChange(
      EditorState.push(editorState, newContent, "insert-fragment"),
      () => {
        if (callbackFn) {
          callbackFn();
        } else {
          this.blockForceFocus(newBlockKey);
        }
      }
    );
  };
  blockClearTypesInFragment = (fragment) => {
    let newFragment = blockMapBuid.createFromArray([]);
    fragment.forEach((block, blockKey) => {
      let updatedBlock = block;
      updatedBlock = updatedBlock.merge({ type: "unstyled" });
      newFragment = newFragment.set(blockKey, updatedBlock);
    });

    return newFragment;
  };
  blockDataCollect = () => {
    const blocks = this.state.editorState
      .getCurrentContent()
      .getBlockMap()
      .toJS();
    let data = {};
    for (let key in blocks) {
      data[key] = blocks[key].data;
    }
    return data;
  };

  blockDataRestore = (blocks) => {
    if (blocks) {
      let editorState = this.state.editorState;
      let contentState = editorState.getCurrentContent();
      const currentSelection = editorState.getSelection();

      for (let key in blocks) {
        const data = blocks[key] || {};
        const blockSelection = SelectionState.createEmpty(key);

        contentState = Modifier.mergeBlockData(
          contentState,
          blockSelection,
          data
        );
      }

      this.onChange(
        EditorState.push(editorState, contentState, "block-data"),
        () => {
          this.onChange(
            EditorState.forceSelection(this.state.editorState, currentSelection)
          );
        }
      );
    }
  };

  /* ------ Save, Load, Import, Export ------ */

  confirmExit = async () => {
    if (!this.state.editorStateChanged) return true;

    const result = await this.context.ask(
      "Есть несохранённые изменения. Сохранить их?",
      "Несохранённые изменения",
      "question"
    );
    if (!result.value) return true;

    return this.onSave(true);
  };

  onOpen = async () => {
    if (!(await this.confirmExit())) return false;
    this.props.onOpen();
  };
  onClose = async () => {
    if (!(await this.confirmExit())) return false;
    this.props.onOpen();
  };
  onNew = async () => {
    if (!(await this.confirmExit())) return false;

    if (this.props.add) {
      this.onChange(EditorState.createEmpty(decorator));
    } else {
      this.props.onNew();
    }
  };
  onSave = async (skipConfirmation = false) => {
    if (!skipConfirmation) {
      const result = await this.context.ask(
        "Сохранить в текущий документ?",
        "Подтверждение сохранения",
        "question"
      );
      if (!result.value) return false;
    }
    if (this.autoSaveTimer) {
      window.clearTimeout(this.autoSaveTimer);
      this.autoSaveTimer = null;
    }
    this.setState({ pending: true });
    let html = await this.htmlSave();
    const saved = await this.props.onSave(
      convertToRaw(this.state.editorState.getCurrentContent()),
      html
    );
    this.setState({
      pending: false,
      editorStateChanged: saved === false ? this.state.editorStateChanged : false,
      autoSaveStatus: saved === false ? this.state.autoSaveStatus : "published",
    });
    return saved !== false;
  };
  onPreview = async () => {
    this.setState({ pending: true });
    let html = await this.htmlSave();

    this.setState({ pending: false, preview: html });
  };
  onPreviewClose = () => {
    this.setState({ preview: false });
  };
  htmlSave = async (contentState = this.state.editorState.getCurrentContent()) => {
    const html = toHTML(contentState);
    let el = document.createElement("html");
    el.innerHTML = html;

    let portals = el.querySelectorAll(".portal-source");
    for (let portal of portals) {
      let key = portal.getAttribute("data-to");
      let gate = el.querySelector(`#portal-gate-${key}`);
      if (gate) {
        gate.innerHTML = portal.innerHTML;
      }
      portal.remove();
    }
    return el.innerHTML;
  };

  /* -------   Tables Staff --------*/

  tableColResize = (step) => {
    const tableInfo = this.tableGetCurrentInfo();
    if (tableInfo) {
      const { row, col } = tableInfo.cellLocation;
      let cell = tableInfo.table[row][col];
      let cellWidth = parseInt(cell.width) || 0;
      let newWidth = cellWidth + step;
      if (newWidth < 1) newWidth = 1;
      if (newWidth > 100) newWidth = 100;

      const resizedTable = tableInfo.table.map((tableRow) =>
        tableRow.map((tableCell, cellCol) =>
          cellCol === col
            ? { ...tableCell, width: newWidth }
            : { ...tableCell }
        )
      );

      this.tableSave(resizedTable);
    }
  };

  //Makes whole row selected
  tableSelectRow = () => {
    const tableInfo = this.tableGetCurrentInfo();
    let selectedCells = [];
    if (tableInfo) {
      const selectedRow = tableInfo.cellLocation.row;
      if (
        tableInfo.table[selectedRow] &&
        Array.isArray(tableInfo.table[selectedRow])
      ) {
        tableInfo.table[selectedRow].forEach((cell) => {
          selectedCells.push(cell.key);
        });
      }
    }
    this.setState({ selectedCells }, () => this.highlightCells());
  };

  //Makes whole col selected
  tableSelectCol = () => {
    const tableInfo = this.tableGetCurrentInfo();
    let selectedCells = [];
    if (tableInfo) {
      const selectedCol = tableInfo.cellLocation.col;
      tableInfo.table.forEach((row) => {
        row.forEach((cell, index) => {
          if (index === selectedCol) {
            selectedCells.push(cell.key);
          }
        });
      });
    }
    this.setState({ selectedCells }, () => this.highlightCells());
  };

  tableMergeCells = () => {
    const tableInfo = this.tableGetCurrentInfo();
    if (!tableInfo) return;
    const result = tableStructure.mergeCells(
      tableInfo.table,
      this.state.selectedCells
    );
    if (!result) {
      if (this.context && this.context.toast) {
        this.context.toast(
          "Выделите прямоугольную область из двух или более не объединённых ячеек",
          "warning"
        );
      }
      return;
    }
    this.tableSave(result.table, {
      newLocation: { key: result.masterKey },
      preserveTableText: true,
    });
  };

  tableSplitCell = () => {
    const tableInfo = this.tableGetCurrentInfo();
    if (!tableInfo) return;
    const result = tableStructure.splitCell(
      tableInfo.table,
      this.state.currentBlock.key
    );
    if (!result) {
      if (this.context && this.context.toast) {
        this.context.toast("Выбранная ячейка не объединена", "warning");
      }
      return;
    }
    this.tableSave(result.table, { newLocation: { key: result.masterKey } });
  };

  //Removes whole row from table & call rebuild
  tableRowRemove = () => {
    const tableInfo = this.tableGetCurrentInfo();
    if (tableInfo) {
      const selectedRow = tableInfo.cellLocation.row;

      if (tableInfo.table.length === 1) {
        this.tableRemove();
        return;
      }
      let newLocation = {
        col: Math.min(tableInfo.cellLocation.col, tableInfo.table[0].length - 1),
        row: Math.min(selectedRow, tableInfo.table.length - 2),
      };
      const newTable = tableStructure.rowRemove(tableInfo.table, selectedRow);
      newLocation.key = newTable[newLocation.row][newLocation.col].key;

      this.tableSave(newTable, {
        newLocation,
      });
    }
  };

  //Removes whole col from table & call rebuild
  tableColRemove = () => {
    const tableInfo = this.tableGetCurrentInfo();
    if (tableInfo) {
      const selectedCol = tableInfo.cellLocation.col;

      if (tableInfo.table[0].length === 1) {
        this.tableRemove();
        return;
      }
      let newLocation = {
        col: Math.min(selectedCol, tableInfo.table[0].length - 2),
        row: Math.min(tableInfo.cellLocation.row, tableInfo.table.length - 1),
      };
      const newTable = tableStructure.colRemove(tableInfo.table, selectedCol);
      newLocation.key = newTable[newLocation.row][newLocation.col].key;

      this.tableSave(newTable, {
        newLocation,
      });
    }
  };

  //Removes table entierly
  tableRemove = (passEditorState) => {
    const tableInfo = this.tableGetCurrentInfo();
    if (tableInfo) {
      let editorState = passEditorState || this.state.editorState;
      const contentState = editorState.getCurrentContent();
      const blockMap = contentState.getBlockMap();
      const tableKeys = tableStructure.getAllKeys(tableInfo.table);

      let beforeFocusKey = contentState.getKeyBefore(tableKeys[0]);
      let afterFocusKey = contentState.getKeyAfter(
        tableKeys[tableKeys.length - 1]
      );

      let newBlockMap = OrderedMap().withMutations((map) => {
        for (let [k, v] of blockMap.entries()) {
          if (!tableKeys.includes(k)) {
            map.set(k, v);
          }
        }
        if (!afterFocusKey && !beforeFocusKey) {
          let blankBlock = this.blockCreateBlank();
          map.set(blankBlock.key, blankBlock);
          afterFocusKey = blankBlock.key;
        }
      });

      const newContent = contentState.merge({
        blockMap: newBlockMap,
      });
      editorState = EditorState.push(
        editorState,
        newContent,
        "remove-fragment"
      );
      if (afterFocusKey || beforeFocusKey) {
        editorState = EditorState.forceSelection(
          editorState,
          SelectionState.createEmpty(afterFocusKey || beforeFocusKey)
        );
      }

      this.onChange(editorState);
    }
  };

  //Returtns table[] for passed block
  tableGetForBlock = (block) => {
    const { editorState } = this.state;
    const contentState = editorState.getCurrentContent();
    const { type, data = {} } = block || {};
    let table = false;
    if (type === "table") {
      table = data.table;
    }
    if (type === "cell") {
      const tableBlock = contentState.getBlockForKey(data.rootKey);
      if (tableBlock) {
        const tableBlockData = tableBlock.getData().toJS();
        table = tableBlockData.table;
      }
    }
    return table;
  };

  //Returns table[] & cell coords for current block
  tableGetCurrentInfo = () => {
    let table = this.tableGetForBlock(this.state.currentBlock);
    if (!table || !Array.isArray(table)) return false;
    table = this.tableSyncContent(table);
    const cellLocation = tableStructure.cellLocate(
      table,
      this.state.currentBlock.key
    );
    return { table, json: JSON.stringify(table), cellLocation };
  };

  //Changes table[] structure to change rows/cols qty
  tableRowColAdd = (direction) => {
    const tableInfo = this.tableGetCurrentInfo();
    if (!tableInfo) return false;

    let table = tableInfo.table;
    if (
      !table ||
      !Array.isArray(table) ||
      !Number.isInteger(tableInfo.cellLocation.row) ||
      !Number.isInteger(tableInfo.cellLocation.col)
    )
      return false;

    // Detect cell location & action. Inserting above the first row or left of
    // the first column is valid and creates a new table root cell.
    const cellLocation = tableInfo.cellLocation;
    let newTable = table;
    if (direction === "up" || direction === "down") {
      newTable = tableStructure.rowAdd(table, cellLocation.row, direction);
    }
    if (direction === "left" || direction === "right") {
      newTable = tableStructure.colAdd(table, cellLocation.col, direction);
    }

    this.tableSave(newTable);
  };

  //Stores table[] at root cell and calls build
  tableSave = (table, saveOptions = {}) => {
    const { editorState } = this.state;
    this.tableBuild(table, editorState, saveOptions);
  };

  // Draft.js keeps the live text in ContentBlocks. Table metadata only stores
  // the shape, so it must be refreshed before a structural operation.
  tableSyncContent = (table, contentState) => {
    if (!Array.isArray(table)) return table;
    const currentContent =
      contentState || this.state.editorState.getCurrentContent();
    return table.map((row) =>
      row.map((cell) => {
        const block = currentContent.getBlockForKey(cell.key);
        return block ? { ...cell, text: block.getText() } : { ...cell };
      })
    );
  };

  //Actually builds and maintains blocks for table[]
  tableBuild = (table = [], passEditorState, saveOptions = {}) => {
    const editorState = passEditorState || this.state.editorState;
    const contentState = editorState.getCurrentContent();
    const blockMap = contentState.getBlockMap();
    table = tableStructure.normaliseRoot(
      saveOptions.preserveTableText
        ? table
        : this.tableSyncContent(table, contentState)
    );
    if (!table.length) return;

    let afterKey = this.state.currentBlock.key;
    const sourceBlock = contentState.getBlockForKey(this.state.currentBlock.key);
    const previousTable = sourceBlock
      ? this.tableGetForBlock({
          key: sourceBlock.getKey(),
          type: sourceBlock.getType(),
          data: sourceBlock.getData().toJS(),
        })
      : this.tableGetForBlock(this.state.currentBlock);
    const previousKeys = new Set(tableStructure.getAllKeys(previousTable));
    const tableRootKey = table[0][0].key;
    const tableKeys = tableStructure.getAllKeys(table);
    const tableBlocks = table.flatMap((row) =>
      row.map((cell) => {
        const currentBlock = contentState.getBlockForKey(cell.key);
        const data = new Map({
          ...(saveOptions.preserveExistingBlockFormatting && currentBlock
            ? currentBlock.getData().toJS()
            : {}),
          ...cell,
          table: cell.self ? table : null,
          rootKey: cell.self ? null : tableRootKey,
        });
        if (
          !currentBlock ||
          (currentBlock.getText() !== cell.text &&
            !saveOptions.preserveExistingBlockFormatting)
        ) {
          return new ContentBlock({
            key: cell.key,
            type: cell.self ? "table" : "cell",
            text: cell.text,
            data,
          });
        }
        return currentBlock
          ? currentBlock.merge({ type: cell.self ? "table" : "cell", data })
          : null;
      })
    );

    let inserted = false;
    const newBlockMap = OrderedMap().withMutations((map) => {
      for (let [key, block] of blockMap.entries()) {
        if (previousKeys.has(key)) {
          if (!inserted) {
            tableBlocks.forEach((tableBlock) =>
              map.set(tableBlock.getKey(), tableBlock)
            );
            inserted = true;
          }
          continue;
        }
        map.set(key, block);
        if (!previousKeys.size && (key === afterKey || !afterKey)) {
          tableBlocks.forEach((tableBlock) =>
            map.set(tableBlock.getKey(), tableBlock)
          );
          inserted = true;
        }
      }
      if (!inserted) {
        tableBlocks.forEach((tableBlock) =>
          map.set(tableBlock.getKey(), tableBlock)
        );
      }
      if (
        map.last() &&
        map.last().getKey() === tableKeys[tableKeys.length - 1]
      ) {
        const blankBlock = this.blockCreateBlank();
        map.set(blankBlock.key, blankBlock);
      }
    });

    if (saveOptions.newLocation && saveOptions.newLocation.key) {
      afterKey = saveOptions.newLocation.key;
    }

    const newContent = contentState.merge({ blockMap: newBlockMap });
    let newEditorState = EditorState.push(
      editorState,
      newContent,
      "insert-fragment"
    );
    const focusKey = newBlockMap.has(afterKey) ? afterKey : tableRootKey;
    newEditorState = EditorState.forceSelection(
      newEditorState,
      SelectionState.createEmpty(focusKey)
    );

    // Do not render an editor state whose selection still points to a cell
    // removed during the table rebuild. Draft.js throws while the toolbar reads
    // the inline style for that stale selection.
    this.onChange(newEditorState, () => {
      const focusedBlock = this.state.editorState
        .getCurrentContent()
        .getBlockForKey(focusKey);
      if (focusedBlock && (focusedBlock.getType() === "table" || focusedBlock.getType() === "cell")) {
        this.setState({ selectedCells: [focusKey] }, () => this.highlightCells());
      }
    });
  };

  //Handles Del & Backspace ops while in table
  tableDeleteHandle = () => {
    const { editorState } = this.state;
    const initialSelection = editorState.getSelection();
    const contentState = editorState.getCurrentContent();

    const initialAnchorKey = initialSelection.getAnchorKey();
    const initialAnchorOffset = initialSelection.getAnchorOffset();
    const initialFocusKey = initialSelection.getFocusKey();
    const initialFocusOffset = initialSelection.getFocusOffset();
    const initialBackward = initialSelection.getIsBackward();

    let newContentState = contentState;

    this.state.selectedBlocks.forEach((blockKey) => {
      let block = contentState.getBlockForKey(blockKey);

      if (block) {
        let blockSelection = SelectionState.createEmpty(blockKey);
        blockSelection = blockSelection.set("focusOffset", block.getLength());

        if (!initialBackward) {
          if (blockKey === initialAnchorKey) {
            blockSelection = blockSelection.set(
              "anchorOffset",
              initialAnchorOffset
            );
          }
          if (blockKey === initialFocusKey) {
            blockSelection = blockSelection.set(
              "focusOffset",
              initialFocusOffset
            );
          }
        } else {
          if (blockKey === initialAnchorKey) {
            blockSelection = blockSelection.set("anchorOffset", 0);
            blockSelection = blockSelection.set(
              "focusOffset",
              initialAnchorOffset
            );
          }
          if (blockKey === initialFocusKey) {
            blockSelection = blockSelection.set(
              "anchorOffset",
              initialFocusOffset
            );
          }
        }

        newContentState = Modifier.replaceText(
          newContentState,
          blockSelection,
          ""
        );
      }
    });

    //Create new selection
    let resetSelection = null;
    resetSelection = SelectionState.createEmpty(initialFocusKey);
    if (initialBackward) {
      resetSelection = resetSelection.set("anchorOffset", initialFocusOffset);
      resetSelection = resetSelection.set("focusOffset", initialFocusOffset);
    }

    //Make & apply new editor state
    let newEditorState = EditorState.push(
      editorState,
      newContentState,
      "insert-fragment"
    );
    let newEditorStateWithSelection = EditorState.forceSelection(
      newEditorState,
      resetSelection
    );

    this.onChange(newEditorStateWithSelection);
  };

  render() {
    const { editorState, inlineToolbar, preview } = this.state;
    return (
      <>
        {preview && (
          <BsModal show xl onClose={this.onPreviewClose}>
            <div
              className="documentViewer fit"
              dangerouslySetInnerHTML={{
                __html: preview,
              }}
            ></div>
          </BsModal>
        )}
        {!this.props.readOnly ? (
          <ToolbarB
            editorFocused={this.state.editorFocused}
            editorState={editorState}
            onToggleInline={this.toggleInlineStyle}
            onFormatPainter={this.onFormatPainterButton}
            formatPainterActive={Boolean(this.state.formatPainter)}
            onOpenFindReplace={this.openFindReplace}
            selectedEntity={this.state.highlightedEntityKey}
            onEntityInsert={this.entityInsert}
            onEntityInsertInline={this.entityInsertInline}
            onToggleBlock={this.toggleBlockType}
            onCreateTable={this.createTable}
            onTogglePreset={this.togglePreset}
            onToggleScale={this.onToggleScale}
            onToggleBlockData={this.onToggleBlockData}
            onCallFunction={this.onCallFunction}
            setLink={this.setLink}
            currentScale={this.state.currentScale}
            pending={this.state.pending}
            onSave={this.props.onSave ? this.onSave : null}
            onOpen={this.props.onOpen ? this.onOpen : null}
            onNew={this.props.onNew ? this.onNew : null}
            onPreview={this.onPreview}
          />
        ) : null}

        {this.renderTableContextMenu()}
        {this.renderTableResizeGuide()}

        <ToolbarA
          editorFocused={this.state.editorFocused}
          editorState={editorState}
          onToggleInline={this.toggleInlineStyle}
          onToggleBlock={this.toggleBlockType}
          onToggleScale={this.onToggleScale}
          setLink={this.setLink}
          currentScale={this.state.currentScale}
          pending={this.state.pending}
          onSave={this.props.onSave ? this.onSave : null}
          onOpen={this.props.onOpen ? this.onOpen : null}
          onClose={this.props.onOpen ? this.onClose : null}
          onNew={this.props.onNew ? this.onNew : null}
          onPreview={this.onPreview}
          autoSaveText={this.getAutoSaveText()}
          onUndo={this.onUndo}
          onRedo={this.onRedo}
          canUndo={editorState.getUndoStack().size > 0}
          canRedo={editorState.getRedoStack().size > 0}
          onDiscardAutoSave={
            this.props.autoSaveAvailable ? this.onDiscardAutoSave : null
          }
        />

        {this.state.showEntityDialog && !this.props.readOnly ? (
          <EntityDialog
            editorState={editorState}
            entityKey={this.state.selectedEntity}
            onHide={this.onEntityDialogHide}
            onSave={this.onEntityDialogSave}
          />
        ) : null}
        {this.state.showPageDialog ? (
          <PageDialog
            pageSettings={this.state.pageSettings}
            onHide={this.onPageDialogHide}
            onSave={this.onPageDialogSave}
          />
        ) : null}
        {this.state.showTableDialog && !this.props.readOnly ? (
          <EditorTableDialog
            block={this.state.currentBlock}
            onHide={this.onTableDialogHide}
            onSave={this.onTableDialogSave}
            currentTable={this.state.currentTable}
          />
        ) : null}
        {this.state.showParagraphDialog && !this.props.readOnly ? (
          <ParagraphDialog
            selectedBlocks={this.state.selectedBlocks}
            block={this.state.currentBlock}
            onHide={this.onParagraphDialogHide}
            onSave={this.onParagraphDialogSave}
          />
        ) : null}

        <div
          id="editor-container"
          style={{ height: this.state.currentEditorHeight || "auto" }}
        >
          {this.renderFindReplace()}
          {this.renderDocumentRulers()}
          <div
            id="editor"
            ref={this.editorRef}
            className={`scale-${this.state.currentScale} ${
              this.state.editorFocused ? "focus" : "blur"
            } ${
              this.state.pageSettings.orientation === "landscape"
                ? "landscape"
                : "portrait"
            }`}
            onDoubleClick={this.editorDblClick}
            onClick={this.editorClick}
            onMouseUp={this.onEditorMouseUp}
          >
            <Editor
              readOnly={this.props.readOnly}
              ref={this.editorComponentRef}
              editorState={editorState}
              onChange={this.onChange}
              customStyleMap={stylemap}
              handlePastedText={this.handlePastedText}
              handleReturn={this.handleReturn}
              handleDroppedFiles={this.handleDroppedFiles}
              keyBindingFn={this.keyBindingFn}
              handleKeyCommand={this.handleKeyCommand}
              blockStyleFn={this.blockStyleFn}
              blockRendererFn={this.blockRenderFn}
              onBlur={this.onBlur}
              onFocus={this.onFocus}
            />
          </div>
        </div>
      </>
    );
  }
}
